/**
 * detail-panel.ts — right-side DOM overlay showing details for the current
 * selection. Content is context-aware:
 *   - kind: 'system' → system summary (planets list, ships-in-system list)
 *   - kind: 'planet' → planet details (biome, size, pop, owner, host system)
 *   - kind: 'ship'   → ship details (owner, chassis, jump state)
 *   - kind: 'none'   → empty-state prompt
 *
 * Ships replaced fleets as the unit of sim — see state/types.ts. Fleets will
 * return later as a pure UI grouping (control groups).
 *
 * Like the HUD, this is a pure reader of GameState + UiState. Click-to-select
 * is handled by input.ts, which mutates UiState.selection.
 */

import {
  EMPIRE_NEUTRAL_ID,
  type Empire,
  type EmpireId,
  type EnvironmentEntity,
  type EnvironmentKind,
  type GameState,
  type Planet,
  type ShipInstance,
  type ShipTemplate,
  type StarSystem,
  type Structure,
  type StructureKind,
  type StructuresData,
  type WarpGateLink,
} from '../state/types';
import type { Command } from '../game/commands';
import {
  buildCreditsForKind,
  buildMineralsForKind,
  isUniqueKind,
  maxLevelForKindByRace,
  maxStructuresForSize,
} from '../game/balance-structures';
import { buildTicksByClass } from '../game/balance-ships';
import { maxShipClassAtPlanet } from '../game/planet-economy';
import {
  asteroidMiningMineralsPerTick,
  CONSTRUCTOR_CHASSIS_BY_RACE,
  gasGiantRefineryFuelPerTick,
  MINEFIELDS_CLOAKED_TECH,
  MINEFIELDS_IFF_TECH,
  MINEFIELDS_SELF_REPLICATING_TECH,
  MINEFIELDS_TECH,
  MINEFIELD_CHASSIS,
  mineCountCap,
  mineDamage,
  minefieldRadius,
  mineTriggerCooldownTicks,
  ODP_PLATFORM_CHASSIS,
  repairYardHullPerTick,
  repairYardRadius,
  warpGateConnectionBuildTicks,
  warpGateConnectionCostFuel,
  warpGateConnectionCostMinerals,
  SPACE_STRUCTURE_KINDS,
  STATIONARY_SPECIAL_ABILITY,
  WARP_GATE_CHASSIS,
  spaceBuildCostForKind,
  spaceBuildTicksForKind,
  spaceSiteRequirementForKind,
} from '../game/balance-space-structures';
import {
  CARRIER_SQUADRON_CAPACITY,
  SQUADRON_CHASSIS_NAMES,
  SQUADRON_FIGHTER_COUNT,
  isCarrierTemplate,
} from '../game/balance-carrier-squadrons';
import {
  BIOME_XLSX_LABEL,
  habitabilityBandFor,
  habitabilityFor,
} from '../game/balance-habitability';
import {
  COLONISER_SPECIAL_ABILITY,
  colonisationRangeFor,
} from '../game/balance-colonisation';
import {
  ADVANCED_TERRAFORMING_TECH,
  TERRAFORMING_TECH,
} from '../game/balance-terraforming';
import {
  effectiveArmor,
  effectiveMaxHull,
  effectiveMaxShields,
  hyperdriveCostMultiplier,
  hyperdriveRangeMultiplier,
  sensorsEmpireBonus,
  sensorsScanRangeMultiplier,
  shieldRegenRate,
  stealthEmpireBonus,
  sublightSpeedMultiplier,
} from '../game/research-effects';
import { cooldownTicksFor, resolveSlotWeapon } from '../game/combat-balance';
import { emittedAuraDef } from '../game/auras';
import { viewerEmpireId, type UiState } from './state';

export interface DetailPanelHandle {
  update(state: GameState, ui: UiState): void;
  destroy(): void;
}

export interface DetailPanelOptions {
  /**
   * Sink for commands dispatched from panel interactions (structure build /
   * upgrade buttons, future surfaces). Omitted in tests that only render
   * the panel. When absent, build / upgrade buttons still render but click
   * events are ignored.
   */
  queueCommand?: (cmd: Command) => void;
}

const PANEL_WIDTH_PX = 260;

export function mountDetailPanel(
  parent: HTMLElement,
  options: DetailPanelOptions = {},
): DetailPanelHandle {
  const root = document.createElement('div');
  root.id = 'detail-panel';
  Object.assign(root.style, {
    position: 'fixed',
    top: '44px', // below the HUD
    right: '0',
    bottom: '0',
    width: `${PANEL_WIDTH_PX}px`,
    padding: '16px',
    background: 'rgba(6,10,20,0.85)',
    borderLeft: '1px solid rgba(80,110,160,0.35)',
    color: '#d8e4ff',
    fontSize: '12px',
    lineHeight: '1.5',
    overflowY: 'auto',
    zIndex: '9',
    pointerEvents: 'auto',
    userSelect: 'text',
    fontVariantNumeric: 'tabular-nums',
  });

  const emptyState = document.createElement('div');
  emptyState.style.color = '#6e80a8';
  emptyState.style.fontStyle = 'italic';
  emptyState.textContent = 'Click a star or planet to inspect.';

  const content = document.createElement('div');

  // Event delegation — buttons are re-created on every tick-re-render, so
  // per-button listeners would be short-lived. A single listener on the
  // content wrapper inspects data-action attributes and dispatches the
  // matching Command through `options.queueCommand`. Listening on
  // `pointerdown` (not `click`) avoids a race where the panel rebuilds
  // between mousedown and mouseup — `click` requires both events to hit
  // the same element, which no longer holds once DOM is replaced mid-press.
  // Keyboard-accessibility trade-off noted: keyboard activation fires
  // `click` but not `pointerdown`; can revisit with a polish pass if needed.
  content.addEventListener('pointerdown', (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const btn = target.closest('[data-action]') as HTMLElement | null;
    if (!btn || btn.hasAttribute('data-disabled')) return;
    const action = btn.dataset.action;
    const queue = options.queueCommand;
    if (!queue) return;
    if (action === 'build-structure') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      const structureKind = btn.dataset.structureKind as StructureKind;
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId) || !structureKind) return;
      queue({ kind: 'buildStructure', empireId, planetId, structureKind });
    } else if (action === 'upgrade-structure') {
      const empireId = Number(btn.dataset.empireId);
      const structureId = Number(btn.dataset.structureId);
      if (!Number.isFinite(empireId) || !Number.isFinite(structureId)) return;
      queue({ kind: 'upgradeStructure', empireId, structureId });
    } else if (action === 'build-ship') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      const chassis = btn.dataset.chassis;
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId) || !chassis) return;
      queue({ kind: 'buildShip', empireId, planetId, chassis });
    } else if (action === 'start-build-placement') {
      // UI-only — mutates UiState.buildPlacement to enter placement mode.
      // The eventual buildSpaceStructure command is dispatched by input.ts
      // when the player clicks a target location in the tactical view.
      const constructorShipId = Number(btn.dataset.constructorShipId);
      const structureKind = btn.dataset.structureKind;
      if (!currentUi) return;
      if (!Number.isFinite(constructorShipId) || !structureKind) return;
      currentUi.buildPlacement = { constructorShipId, structureKind };
    } else if (action === 'start-connect-warp-gate') {
      // UI-only — mutates UiState.connectWarpGateMode to enter the
      // target picker. The eventual connectWarpGate command is
      // dispatched by input.ts when the player LMBs a target system
      // in galaxy view.
      const fromGateId = Number(btn.dataset.gateId);
      const fromSystemId = Number(btn.dataset.systemId);
      if (!currentUi) return;
      if (!Number.isFinite(fromGateId) || !Number.isFinite(fromSystemId)) return;
      currentUi.connectWarpGateMode = { fromGateId, fromSystemId };
    } else if (action === 'disconnect-warp-gate') {
      const empireId = Number(btn.dataset.empireId);
      const linkId = Number(btn.dataset.linkId);
      if (!Number.isFinite(empireId) || !Number.isFinite(linkId)) return;
      queue({ kind: 'disconnectWarpGate', empireId, linkId });
    } else if (action === 'start-terraform') {
      // MFix4.3 — empty `coloniserShipId` data attribute → null
      // (Advanced Terraforming L6 path); non-empty → ShipInstanceId
      // (Coloniser path, ship despawned on success).
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      const csidRaw = btn.dataset.coloniserShipId ?? '';
      const coloniserShipId = csidRaw === '' ? null : Number(csidRaw);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      if (coloniserShipId !== null && !Number.isFinite(coloniserShipId)) return;
      queue({
        kind: 'startTerraform',
        empireId,
        targetPlanetId: planetId,
        coloniserShipId,
      });
    } else if (action === 'launch-carrier-squadrons') {
      // v3M17.4 — manual launch override on the selected carrier.
      const empireId = Number(btn.dataset.empireId);
      const carrierShipId = Number(btn.dataset.carrierShipId);
      if (!Number.isFinite(empireId) || !Number.isFinite(carrierShipId)) return;
      queue({ kind: 'launchCarrierSquadrons', empireId, carrierShipId });
    } else if (action === 'recall-carrier-squadrons') {
      // v3M17.4 — manual recall override on the selected carrier.
      const empireId = Number(btn.dataset.empireId);
      const carrierShipId = Number(btn.dataset.carrierShipId);
      if (!Number.isFinite(empireId) || !Number.isFinite(carrierShipId)) return;
      queue({ kind: 'recallCarrierSquadrons', empireId, carrierShipId });
    } else if (action === 'set-carrier-attack') {
      // v3M17.4 — Attack Behaviour selector.
      const empireId = Number(btn.dataset.empireId);
      const carrierShipId = Number(btn.dataset.carrierShipId);
      const behaviour = btn.dataset.behaviour as
        | 'none' | 'visual-range' | 'scan-range' | undefined;
      if (
        !Number.isFinite(empireId) ||
        !Number.isFinite(carrierShipId) ||
        (behaviour !== 'none' && behaviour !== 'visual-range' && behaviour !== 'scan-range')
      ) {
        return;
      }
      queue({
        kind: 'setCarrierAttackBehaviour',
        empireId,
        carrierShipId,
        behaviour,
      });
    } else if (action === 'set-carrier-defense') {
      // v3M17.4 — Defense Behaviour selector.
      const empireId = Number(btn.dataset.empireId);
      const carrierShipId = Number(btn.dataset.carrierShipId);
      const behaviour = btn.dataset.behaviour as
        | 'return-at-low' | 'no-surrender' | undefined;
      if (
        !Number.isFinite(empireId) ||
        !Number.isFinite(carrierShipId) ||
        (behaviour !== 'return-at-low' && behaviour !== 'no-surrender')
      ) {
        return;
      }
      queue({
        kind: 'setCarrierDefenseBehaviour',
        empireId,
        carrierShipId,
        behaviour,
      });
    } else if (action === 'set-ship-patrol') {
      // v3M22.4 — start a Patrol command on the selected ship.
      const empireId = Number(btn.dataset.empireId);
      const shipId = Number(btn.dataset.shipId);
      if (!Number.isFinite(empireId) || !Number.isFinite(shipId)) return;
      queue({ kind: 'setShipPatrol', empireId, shipId });
    } else if (action === 'set-ship-explore') {
      // v3M22.4 — start an Explore command on the selected ship.
      const empireId = Number(btn.dataset.empireId);
      const shipId = Number(btn.dataset.shipId);
      if (!Number.isFinite(empireId) || !Number.isFinite(shipId)) return;
      queue({ kind: 'setShipExplore', empireId, shipId });
    } else if (action === 'start-ship-guard') {
      // v3M22.4 — UI-only: enter the Guard target-picker. The eventual
      // setShipGuard Command is dispatched by input.ts when the player
      // LMBs a planet or friendly ship in the system view.
      const shipId = Number(btn.dataset.shipId);
      const systemId = Number(btn.dataset.systemId);
      if (!currentUi) return;
      if (!Number.isFinite(shipId) || !Number.isFinite(systemId)) return;
      currentUi.guardTargetMode = { shipId, systemId };
      currentUi.buildPlacement = null;
      currentUi.connectWarpGateMode = null;
    } else if (action === 'clear-ship-command') {
      // v3M22.4 — Stop button: clears any active Patrol / Explore /
      // Guard. Also exits guard-target mode if the player happened to
      // be in it for this ship.
      const empireId = Number(btn.dataset.empireId);
      const shipId = Number(btn.dataset.shipId);
      if (!Number.isFinite(empireId) || !Number.isFinite(shipId)) return;
      queue({ kind: 'clearShipCommand', empireId, shipId });
      if (currentUi?.guardTargetMode?.shipId === shipId) {
        currentUi.guardTargetMode = null;
      }
    } else if (action === 'set-diplomatic-focus') {
      // v3M21.6.4 — focus this neutral planet (or null to clear).
      const empireId = Number(btn.dataset.empireId);
      const raw = btn.dataset.planetId ?? '';
      const planetId = raw === '' ? null : Number(raw);
      if (!Number.isFinite(empireId)) return;
      if (planetId !== null && !Number.isFinite(planetId)) return;
      queue({ kind: 'setDiplomaticFocus', empireId, planetId });
    } else if (action === 'transfer-diplomatic') {
      // v3M21.6.4 — one-shot dump of the empire's full Diplomatic
      // treasury into the focused neutral's per-empire pool. The
      // command applier validates focus + amount; rejecting silently
      // when there's nothing to transfer is fine.
      const empireId = Number(btn.dataset.empireId);
      const amount = Number(btn.dataset.amount);
      if (!Number.isFinite(empireId) || !Number.isFinite(amount)) return;
      queue({ kind: 'transferDiplomaticToFocus', empireId, amount });
    } else if (action === 'invoke-annex') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'invokeAnnex', empireId, planetId });
    } else if (action === 'invoke-trade') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'invokeTrade', empireId, planetId });
    } else if (action === 'clear-trade') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'clearTrade', empireId, planetId });
    } else if (action === 'invoke-federation') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'invokeFederation', empireId, planetId });
    } else if (action === 'clear-federation') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'clearFederation', empireId, planetId });
    } else if (action === 'invoke-vassal') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'invokeVassal', empireId, planetId });
    } else if (action === 'clear-vassal') {
      const empireId = Number(btn.dataset.empireId);
      const planetId = Number(btn.dataset.planetId);
      if (!Number.isFinite(empireId) || !Number.isFinite(planetId)) return;
      queue({ kind: 'clearVassal', empireId, planetId });
    }
  });

  root.appendChild(emptyState);
  root.appendChild(content);
  parent.appendChild(root);

  let lastRenderedKey = '';
  // Latest UiState reference, captured on each update(). The pointerdown
  // handler reads this to mutate UiState.buildPlacement (which is UI-local,
  // not a sim Command, so it doesn't flow through queueCommand).
  let currentUi: UiState | null = null;

  return {
    update(state: GameState, ui: UiState): void {
      currentUi = ui;
      const sel = ui.selection;
      if (sel.kind === 'none') {
        emptyState.style.display = '';
        content.style.display = 'none';
        lastRenderedKey = '';
        return;
      }
      emptyState.style.display = 'none';
      content.style.display = '';

      // Cheap re-render key — only rebuild the DOM if the visible contents
      // might have changed. Tick number covers pop + jump progress; kind + id
      // cover selection changes.
      const key = `${sel.kind}|${'id' in sel ? sel.id : '-'}|${state.tick}`;
      if (key === lastRenderedKey) return;
      lastRenderedKey = key;

      content.innerHTML = '';

      if (sel.kind === 'system') {
        const sys = state.systems.get(sel.id);
        if (!sys) return renderMissing(content, 'system');
        renderSystem(content, state, sys, viewerEmpireId(state, ui), ui);
      } else if (sel.kind === 'planet') {
        const planet = state.planets.get(sel.id);
        if (!planet) return renderMissing(content, 'planet');
        renderPlanet(content, state, planet, viewerEmpireId(state, ui));
      } else if (sel.kind === 'environment') {
        const env = state.environment.get(sel.id);
        if (!env) return renderMissing(content, 'terrain feature');
        renderEnvironment(content, state, env);
      } else {
        const ship = state.ships.get(sel.id);
        if (!ship) return renderMissing(content, 'ship');
        renderShip(content, state, ship);
      }
    },
    destroy(): void {
      root.remove();
    },
  };
}

function renderSystem(
  host: HTMLElement,
  state: GameState,
  sys: StarSystem,
  viewer: number,
  ui: UiState,
): void {
  host.innerHTML = '';

  // MFix6.3 — System detail panel respects galaxy-view fog-of-war.
  // Unscouted systems render a header with the system shown as "?"
  // (matching the galaxy-view label) plus a single "Unscouted" line
  // and stop. Coords stay hidden — they're "info" too. devRevealAll
  // (F10 cheat) bypasses the gate so QA can still inspect.
  const viewerEmpire = state.empires.get(viewer);
  const scouted =
    ui.devRevealAll || (viewerEmpire?.scoutedSystemIds.has(sys.id) ?? false);
  if (!scouted) {
    host.appendChild(section('SYSTEM', '?'));
    host.appendChild(
      descriptionBlock(
        'Unscouted system. Send a ship into this system to reveal its planets, jump points, and any garrisoned forces.',
      ),
    );
    return;
  }

  host.appendChild(section('SYSTEM', sys.name));

  const meta = document.createElement('div');
  meta.style.color = '#8ea0c8';
  meta.style.marginBottom = '12px';
  meta.textContent = `( ${sys.x.toFixed(3)}, ${sys.y.toFixed(3)} )`;
  host.appendChild(meta);

  // v3M15.3 — Overview stats: planet count + per-empire ownership
  // breakdown, ships-here / inbound counts, jump-point count, terrain
  // feature count. Each row is the count, followed by a list-view of
  // the same population further down. Names suffixed `…Count` to avoid
  // shadowing the `shipsHere` / `inbound` arrays in the list section.
  let shipsHereCount = 0;
  let shipsInboundCount = 0;
  for (const ship of state.ships.values()) {
    if (ship.systemId === sys.id) shipsHereCount += 1;
    else if (ship.jumpTargetSystemId === sys.id) shipsInboundCount += 1;
  }
  let envCount = 0;
  for (const env of state.environment.values()) {
    if (env.systemId === sys.id) envCount += 1;
  }
  const ownerCounts = new Map<number | null, number>();
  for (const pid of sys.planetIds) {
    const p = state.planets.get(pid);
    if (!p) continue;
    ownerCounts.set(p.ownerEmpireId, (ownerCounts.get(p.ownerEmpireId) ?? 0) + 1);
  }
  const ownerSummary: string[] = [];
  for (const [empireId, count] of ownerCounts) {
    if (empireId === null) ownerSummary.push(`${count} neutral`);
    else {
      const empire = state.empires.get(empireId);
      ownerSummary.push(`${count} ${empire?.name ?? `#${empireId}`}`);
    }
  }
  host.appendChild(sectionHeader('Overview'));
  host.appendChild(
    labelValue(
      'Planets',
      ownerSummary.length > 0
        ? `${sys.planetIds.length} (${ownerSummary.join(' · ')})`
        : `${sys.planetIds.length}`,
    ),
  );
  host.appendChild(labelValue('Ships in system', `${shipsHereCount}`));
  if (shipsInboundCount > 0) {
    host.appendChild(labelValue('Inbound ships', `${shipsInboundCount}`));
  }
  host.appendChild(labelValue('Adjacent systems', `${sys.adjacentSystemIds.length}`));
  host.appendChild(labelValue('Terrain features', `${envCount}`));

  // v3M18 — Warp Gate connection control. Shown when the viewer empire
  // owns an operational Warp Gate in this system. Three button states:
  //   - Unconnected, sufficient resources → "Connect Warp Gate" enters
  //     target-picker mode.
  //   - Unconnected, insufficient resources → disabled with cost hint.
  //   - Building → disabled with progress readout (N/600).
  //   - Live link → "Disconnect Warp Gate" fires command immediately.
  if (viewerEmpire) {
    let viewerGate: ShipInstance | null = null;
    for (const s of state.ships.values()) {
      if (s.ownerEmpireId !== viewer) continue;
      if (s.systemId !== sys.id) continue;
      if (s.chassis !== WARP_GATE_CHASSIS) continue;
      viewerGate = s;
      break;
    }
    if (viewerGate) {
      let existingLink: WarpGateLink | null = null;
      for (const link of state.warpGateLinks.values()) {
        if (
          link.gateAId === viewerGate.id ||
          link.gateBId === viewerGate.id
        ) {
          existingLink = link;
          break;
        }
      }
      host.appendChild(sectionHeader('Warp Gate Connection'));
      const sd = state.structuresData;
      const costMin = warpGateConnectionCostMinerals(sd);
      const costFuel = warpGateConnectionCostFuel(sd);
      const buildTicks = warpGateConnectionBuildTicks(sd);
      if (existingLink === null) {
        const canAfford =
          viewerEmpire.resources.minerals >= costMin &&
          viewerEmpire.resources.fuel >= costFuel;
        const btn = document.createElement('button');
        btn.textContent = 'Connect Warp Gate';
        btn.style.padding = '6px 12px';
        btn.style.background = canAfford ? '#3a4a6e' : '#2a3146';
        btn.style.color = canAfford ? '#d8e4ff' : '#5e6a85';
        btn.style.border = '1px solid #4a5a85';
        btn.style.borderRadius = '4px';
        btn.style.cursor = canAfford ? 'pointer' : 'not-allowed';
        btn.style.fontSize = '13px';
        btn.style.marginTop = '4px';
        btn.dataset.action = 'start-connect-warp-gate';
        btn.dataset.gateId = String(viewerGate.id);
        btn.dataset.systemId = String(sys.id);
        if (!canAfford) btn.dataset.disabled = '1';
        btn.title = canAfford
          ? 'Begin a warp-gate connection from this gate to another of your gates. Pick the target gate next.'
          : `Not enough resources — needs ${costMin.toLocaleString()} minerals and ${costFuel.toLocaleString()} fuel.`;
        host.appendChild(btn);
        host.appendChild(
          dim(
            `Cost: ${costMin.toLocaleString()} minerals + ${costFuel.toLocaleString()} fuel · ${buildTicks} ticks`,
          ),
        );
      } else if (existingLink.status === 'building') {
        host.appendChild(
          labelValue(
            'Connecting…',
            `${existingLink.progressTicks} / ${existingLink.targetTicks} ticks`,
          ),
        );
        // Disconnect is allowed during building (instant + free).
        const btn = document.createElement('button');
        btn.textContent = 'Cancel / Disconnect';
        btn.style.padding = '6px 12px';
        btn.style.background = '#5a3a3a';
        btn.style.color = '#f0d8d8';
        btn.style.border = '1px solid #855a5a';
        btn.style.borderRadius = '4px';
        btn.style.cursor = 'pointer';
        btn.style.fontSize = '13px';
        btn.style.marginTop = '4px';
        btn.dataset.action = 'disconnect-warp-gate';
        btn.dataset.empireId = String(viewer);
        btn.dataset.linkId = String(existingLink.id);
        btn.title = 'Cancel the in-progress warp-gate connection. Instant and free; no cost refund.';
        host.appendChild(btn);
      } else {
        // Live — show partner system name + Disconnect button.
        const partnerSysId =
          existingLink.systemAId === sys.id
            ? existingLink.systemBId
            : existingLink.systemAId;
        const partnerSys = state.systems.get(partnerSysId);
        host.appendChild(
          labelValue(
            'Connected to',
            partnerSys?.name ?? `system #${partnerSysId}`,
          ),
        );
        const btn = document.createElement('button');
        btn.textContent = 'Disconnect Warp Gate';
        btn.style.padding = '6px 12px';
        btn.style.background = '#5a3a3a';
        btn.style.color = '#f0d8d8';
        btn.style.border = '1px solid #855a5a';
        btn.style.borderRadius = '4px';
        btn.style.cursor = 'pointer';
        btn.style.fontSize = '13px';
        btn.style.marginTop = '4px';
        btn.dataset.action = 'disconnect-warp-gate';
        btn.dataset.empireId = String(viewer);
        btn.dataset.linkId = String(existingLink.id);
        btn.title = 'Sever the live warp-gate connection. Instant and free; both gates remain but unlinked.';
        host.appendChild(btn);
      }
    }
  }

  // Planets.
  host.appendChild(sectionHeader('Planets'));
  if (sys.planetIds.length === 0) {
    host.appendChild(dim('— none —'));
  } else {
    for (const pid of sys.planetIds) {
      const planet = state.planets.get(pid);
      if (!planet) continue;
      host.appendChild(renderPlanetRow(state, planet));
    }
  }

  // Ships currently in this system, plus any inbound. Both are grouped
  // together so the player can see who's here and who's on the way without
  // hunting across two lists. Ships mid-jump to somewhere else are excluded.
  host.appendChild(sectionHeader('Ships'));
  const shipsHere: ShipInstance[] = [];
  const inbound: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.systemId === sys.id) shipsHere.push(ship);
    else if (ship.jumpTargetSystemId === sys.id) inbound.push(ship);
  }
  if (shipsHere.length === 0 && inbound.length === 0) {
    host.appendChild(dim('— none —'));
  } else {
    for (const ship of shipsHere) host.appendChild(renderShipRow(state, ship, sys.id));
    for (const ship of inbound) host.appendChild(renderShipRow(state, ship, sys.id));
  }
}

/**
 * v3M15.1 — Per-kind display label for an environment entity. Returns the
 * heading text shown in the panel section header (e.g. "ASTEROID FIELD").
 */
const ENVIRONMENT_DISPLAY_NAME: Readonly<Record<EnvironmentKind, string>> = Object.freeze({
  'asteroid-field': 'Asteroid Field',
  'gas-giant': 'Gas Giant',
  nebula: 'Nebula',
  'ion-cloud': 'Ion Cloud',
  'radiation-belt': 'Radiation Belt',
  'black-hole': 'Black Hole',
});

/**
 * v3M15.1 — Render a terrain feature (EnvironmentEntity) in the detail
 * panel. Per-kind description + mechanical effect summary so the player
 * understands what the colored circle in system view actually does.
 *
 * Asteroid fields and gas giants are extractor build sites — surface the
 * matching Constructor structure + per-tick output. The four MFix4.2
 * cosmetic kinds (nebula / ion-cloud / radiation-belt / black-hole)
 * currently have no mechanical effect; the panel says so explicitly so
 * the player isn't left guessing.
 */
function renderEnvironment(
  host: HTMLElement,
  state: GameState,
  env: EnvironmentEntity,
): void {
  const displayName = ENVIRONMENT_DISPLAY_NAME[env.kind];
  host.appendChild(section('TERRAIN FEATURE', displayName));

  const sys = state.systems.get(env.systemId);
  const meta = document.createElement('div');
  meta.style.color = '#8ea0c8';
  meta.style.marginBottom = '12px';
  meta.textContent = sys ? `in ${sys.name}` : `system #${env.systemId}`;
  host.appendChild(meta);

  host.appendChild(sectionHeader('Overview'));
  host.appendChild(labelValue('Position', `( ${env.sx.toFixed(2)}, ${env.sy.toFixed(2)} )`));
  host.appendChild(labelValue('Radius', env.radius.toFixed(3)));

  // Per-kind effect block.
  host.appendChild(sectionHeader('Effect'));
  const sd = state.structuresData;
  switch (env.kind) {
    case 'asteroid-field': {
      const rate = asteroidMiningMineralsPerTick(sd);
      host.appendChild(
        descriptionBlock(
          'Build site for the Asteroid Mining Outpost — a Constructor parked anywhere inside this footprint can erect the extractor.',
        ),
      );
      host.appendChild(labelValue('Mining yield', `+${rate} minerals / tick`));
      break;
    }
    case 'gas-giant': {
      const rate = gasGiantRefineryFuelPerTick(sd);
      host.appendChild(
        descriptionBlock(
          'Build site for the Gas Giant Refinery — a Constructor parked anywhere inside this footprint can erect the extractor.',
        ),
      );
      host.appendChild(labelValue('Refinery yield', `+${rate} fuel / tick`));
      break;
    }
    case 'nebula':
      host.appendChild(
        descriptionBlock(
          'Decorative gas cloud. No mechanical effect in v1 — sensor / cloak / movement interactions are reserved for a future balance pass.',
        ),
      );
      break;
    case 'ion-cloud':
      host.appendChild(
        descriptionBlock(
          'Decorative plasma pocket. No mechanical effect in v1 — designed as an EMP / energy hazard but unwired so far.',
        ),
      );
      break;
    case 'radiation-belt':
      host.appendChild(
        descriptionBlock(
          'Decorative radiation hazard. No mechanical effect in v1 — designed as a hull DoT or electronics-interference zone for a future pass.',
        ),
      );
      break;
    case 'black-hole':
      host.appendChild(
        descriptionBlock(
          'Decorative galactic landmark. No mechanical effect in v1 — gravitational pull / hyperspace disruption reserved for a future pass.',
        ),
      );
      break;
  }
}

/**
 * Multi-line descriptive paragraph for the terrain feature panel and
 * any future panel sections that need a wrapping prose block. Uses the
 * dim caption color so it reads as commentary rather than a stat row.
 */
function descriptionBlock(text: string): HTMLElement {
  const p = document.createElement('div');
  p.textContent = text;
  p.style.color = '#a8b6d0';
  p.style.fontSize = '11px';
  p.style.lineHeight = '1.4';
  p.style.padding = '4px 0 6px';
  return p;
}

function section(label: string, value: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.marginBottom = '4px';
  const labelEl = document.createElement('div');
  labelEl.textContent = label;
  labelEl.style.fontSize = '10px';
  labelEl.style.letterSpacing = '0.08em';
  labelEl.style.color = '#7a8bb5';
  const valueEl = document.createElement('div');
  valueEl.textContent = value;
  valueEl.style.fontSize = '16px';
  valueEl.style.fontWeight = '600';
  wrap.appendChild(labelEl);
  wrap.appendChild(valueEl);
  return wrap;
}

function sectionHeader(label: string): HTMLElement {
  const h = document.createElement('div');
  h.textContent = label;
  h.style.marginTop = '16px';
  h.style.marginBottom = '4px';
  h.style.fontSize = '10px';
  h.style.letterSpacing = '0.08em';
  h.style.color = '#7a8bb5';
  h.style.borderBottom = '1px solid rgba(80,110,160,0.25)';
  h.style.paddingBottom = '2px';
  return h;
}

function dim(text: string): HTMLElement {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.color = '#6e80a8';
  el.style.fontStyle = 'italic';
  return el;
}

function renderPlanetRow(state: GameState, planet: Planet): HTMLElement {
  const row = document.createElement('div');
  row.style.padding = '4px 0';
  row.style.borderBottom = '1px dashed rgba(80,110,160,0.15)';

  const name = document.createElement('div');
  name.textContent = planet.name;
  name.style.fontWeight = '600';

  const meta = document.createElement('div');
  meta.style.color = '#8ea0c8';
  meta.style.fontSize = '11px';
  const owner = ownerName(state, planet.ownerEmpireId);
  meta.textContent =
    `${planet.biome} · Size ${planet.size} · pop ${planet.currentPopulation.toFixed(1)}/` +
    `${planet.populationCap} · ${owner}`;

  row.appendChild(name);
  row.appendChild(meta);
  return row;
}

function renderShipRow(state: GameState, ship: ShipInstance, sysId: number): HTMLElement {
  const row = document.createElement('div');
  row.style.padding = '4px 0';
  row.style.borderBottom = '1px dashed rgba(80,110,160,0.15)';

  const owner = state.empires.get(ship.ownerEmpireId);

  const name = document.createElement('div');
  name.textContent = owner ? `${owner.name} ${ship.chassis}` : ship.chassis;
  name.style.fontWeight = '600';
  name.style.color = owner?.isPlayer ? '#8fffb0' : '#ff8f8f';

  const status = document.createElement('div');
  status.style.color = '#8ea0c8';
  status.style.fontSize = '11px';
  if (ship.jumpTargetSystemId === sysId) {
    status.textContent = `inbound · ${ship.jumpTicksRemaining} ticks`;
  } else if (ship.jumpTargetSystemId !== null) {
    const target = state.systems.get(ship.jumpTargetSystemId);
    status.textContent = `jumping to ${target?.name ?? '??'} · ${ship.jumpTicksRemaining} ticks`;
  } else {
    status.textContent = 'in system';
  }

  row.appendChild(name);
  row.appendChild(status);
  return row;
}

function ownerName(state: GameState, empireId: number | null): string {
  if (empireId === null) return 'uninhabited';
  const empire: Empire | undefined = state.empires.get(empireId);
  return empire ? empire.name : `empire #${empireId}`;
}

function renderMissing(host: HTMLElement, what: string): void {
  host.innerHTML = '';
  const missing = document.createElement('div');
  missing.style.color = '#a65a5a';
  missing.textContent = `Selected ${what} no longer exists.`;
  host.appendChild(missing);
}

function renderPlanet(
  host: HTMLElement,
  state: GameState,
  planet: Planet,
  viewer: number,
): void {
  host.appendChild(section('PLANET', planet.name));

  const sys = state.systems.get(planet.systemId);
  const meta = document.createElement('div');
  meta.style.color = '#8ea0c8';
  meta.style.marginBottom = '12px';
  meta.textContent = sys ? `in ${sys.name}` : `system #${planet.systemId}`;
  host.appendChild(meta);

  host.appendChild(sectionHeader('Overview'));
  host.appendChild(labelValue('Biome', planet.biome));
  host.appendChild(labelValue('Size', String(planet.size)));
  host.appendChild(
    labelValue('Population', `${planet.currentPopulation.toFixed(2)} / ${planet.populationCap}`),
  );
  // v3M15.3 — Structure-slot usage. Helps players see at a glance how
  // much room is left for new builds.
  host.appendChild(
    labelValue(
      'Structure slots',
      `${planet.structureIds.length}/${maxStructuresForSize(planet.size)}`,
    ),
  );
  host.appendChild(labelValue('Owner', ownerName(state, planet.ownerEmpireId)));

  // v2M5: Loyalty is owner-only per PLANETS §2.7 — attackers infer
  // bombardment progress indirectly. Visible to whichever empire owns
  // the planet from the *viewer's* POV — F10 enemy-POV swaps the
  // viewer to a non-player empire, so an AI's Loyalty surfaces while
  // debugging from their POV. Hidden otherwise.
  if (planet.ownerEmpireId === viewer) {
    host.appendChild(loyaltyRow(planet.loyalty));
  }

  // v3M15.3 — Bombardment status: the defender (planet owner) sees who
  // is currently bombarding them and how recently. Same FoW posture as
  // Loyalty above — attackers don't see this, only defenders. The sim
  // clears `lastBombardingEmpireId` once bombardment ceases (see
  // advance-bombardment.ts), so a non-null value means an active hostile
  // is firing right now or did within the last batch window.
  if (
    planet.ownerEmpireId === viewer &&
    planet.lastBombardingEmpireId !== null
  ) {
    const attacker = state.empires.get(planet.lastBombardingEmpireId);
    const lastTick = planet.lastBombardedTick;
    const ticksAgo = lastTick !== null ? state.tick - lastTick : null;
    const ticksLabel =
      ticksAgo !== null ? `last hit ${ticksAgo} tick${ticksAgo === 1 ? '' : 's'} ago` : 'in progress';
    host.appendChild(
      descriptionBlock(
        `⚠ Under bombardment by ${attacker?.name ?? 'unknown'} — ${ticksLabel}.`,
      ),
    );
  }

  // MFix3.5 — Habitability score for the viewer's race. Visible on every
  // planet (owned, neutral, or enemy) so the player can evaluate a
  // colonisation target before committing a Coloniser. Format:
  // "75 (Hospitable)" with the band coloured per band severity.
  renderPlanetHabitability(host, state, planet, viewer);

  if (planet.terraforming) {
    host.appendChild(sectionHeader('Terraforming'));
    host.appendChild(labelValue('Target biome', planet.terraforming.targetBiome));
    host.appendChild(labelValue('Ticks left', `${planet.terraforming.ticksRemaining}`));
    host.appendChild(
      labelValue(
        'Cost',
        `fuel ${planet.terraforming.fuelCost} · minerals ${planet.terraforming.mineralsCost}`,
      ),
    );
  } else {
    // MFix4.3 — terraform start button. Two paths surface the same
    // section, gated by tech + planet ownership state:
    //   - Coloniser path (Terraforming L4): uninhabited non-Gaia planet,
    //     a player-owned Coloniser within range. Ship despawned on
    //     success (matches the colonise pattern).
    //   - Planet-only path (Advanced Terraforming L6): empire-owned
    //     non-Gaia planet, no ship required.
    renderTerraformSection(host, state, planet, viewer);
  }

  // v3M21.6 — Diplomatic Influence section. Surfaces only on neutral
  // planets (`planet.ownerEmpireId === EMPIRE_NEUTRAL_ID`). Per
  // `DIPLOMACY.md §10`, owned planets are unchanged in v3M21 — the
  // existing Loyalty + Bombardment + Habitability rows above carry
  // forward without modification.
  renderPlanetDiplomacy(host, state, planet, viewer);

  renderPlanetStructures(host, state, planet);
  renderPlanetShipyard(host, state, planet);
}

// ---------------------------------------------------------------------------
// v3M21.6 — Diplomatic Influence section
// ---------------------------------------------------------------------------

/**
 * Per-empire Influence bars + threshold ticks + Federation / Vassal /
 * Trade banners on a neutral planet. Read-only in v3M21.6.3 — buttons
 * land in v3M21.6.4. Layout mirrors `DIPLOMACY.md §10`:
 *
 * ```
 * DIPLOMATIC INFLUENCE
 *  You:        [████████░░░░] 480 / 800   (Federation unlocked)
 *  Empire X:   [██░░░░░░░░░░] 120 / 800
 *                ↑    ↑    ↑    ↑
 *              Trade Fed Vassal Annex
 *               200  400  600   800
 * ```
 *
 * Visibility rule: the viewer's own bar always renders (even at 0); a
 * rival empire's bar renders iff that rival has a non-zero entry in
 * the planet's Influence Map. Synthetic neutral never appears.
 */
function renderPlanetDiplomacy(
  host: HTMLElement,
  state: GameState,
  planet: Planet,
  viewer: number,
): void {
  if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) return;

  host.appendChild(sectionHeader('Diplomatic Influence'));

  const thresholds = state.balanceData.diplomacy.thresholds;
  const annexThreshold = thresholds.annex;

  // Empires to render: viewer first (always), then any rival with a
  // non-zero entry in Planet.influence. Synthetic neutral excluded.
  const empireIds: EmpireId[] = [];
  if (state.empires.has(viewer) && viewer !== EMPIRE_NEUTRAL_ID) {
    empireIds.push(viewer);
  }
  for (const [eid, value] of planet.influence) {
    if (eid === viewer || eid === EMPIRE_NEUTRAL_ID) continue;
    if (value <= 0) continue;
    empireIds.push(eid);
  }

  for (const eid of empireIds) {
    host.appendChild(
      influenceBarRow(state, planet, eid, viewer, thresholds, annexThreshold),
    );
  }

  host.appendChild(thresholdLegend(thresholds, annexThreshold));

  // Banners — Federation / Vassal / Trade. Each renders iff the planet
  // is currently in that state with at least one empire.
  if (planet.federatedWith !== null) {
    const partner = state.empires.get(planet.federatedWith);
    host.appendChild(
      diplomacyBanner(
        `Federated with ${partner?.name ?? 'unknown'} — non-federated influence pools capped at ${thresholds.federation}.`,
        '#4FB3FF',
      ),
    );
  }
  if (planet.vassalOf !== null) {
    const suzerain = state.empires.get(planet.vassalOf);
    const fraction = state.balanceData.diplomacy.vassalTributeFraction;
    const pct = Math.round(fraction * 100);
    host.appendChild(
      diplomacyBanner(
        `Vassal of ${suzerain?.name ?? 'unknown'} — ${pct}% of resource production routed.`,
        '#F0A93B',
      ),
    );
  }
  if (planet.tradingWith.length > 0) {
    const labels = planet.tradingWith
      .map((eid) => (eid === viewer ? 'You' : (state.empires.get(eid)?.name ?? '?')))
      .join(', ');
    host.appendChild(diplomacyBanner(`Trading with: ${labels}.`, '#63C77C'));
  }

  // v3M21.6.4 — action buttons. Skips for the synthetic neutral
  // viewer (defensive) — diplomacy commands all reject neutral
  // empire-id at the applier level anyway.
  if (viewer !== EMPIRE_NEUTRAL_ID && state.empires.has(viewer)) {
    host.appendChild(renderPlanetDiplomacyActions(state, planet, viewer, thresholds));
  }
}

/** v3M21.6.5 — pure UI-state model for the four outcome buttons. The
 *  detail panel renders this; tests exercise it without a DOM. */
export interface DiplomacyButtonState {
  /** Button label as drawn — flips between "Invoke X" / "End X" /
   *  "Stop X" forms depending on the current planet state. */
  label: string;
  /** `null` when enabled; otherwise a short user-facing reason that
   *  becomes the disabled-button tooltip. */
  disabledReason: string | null;
  /** Action id the pointerdown router dispatches against. */
  action:
    | 'invoke-trade'
    | 'clear-trade'
    | 'invoke-federation'
    | 'clear-federation'
    | 'invoke-vassal'
    | 'clear-vassal'
    | 'invoke-annex';
}

export interface DiplomacyButtonStates {
  trade: DiplomacyButtonState;
  federation: DiplomacyButtonState;
  vassal: DiplomacyButtonState;
  annex: DiplomacyButtonState;
}

/**
 * v3M21.6.5 — pure helper computing the four diplomacy outcome
 * buttons' label / action / disabled-reason from a single planet +
 * viewer + threshold tuple. Mirrors the command-applier validation
 * rules in `commands.ts §setDiplomaticFocus / invokeAnnex / …`:
 * threshold gates, federation supersedes vassal, federation locked
 * to a rival, etc.
 *
 * The applier still validates server-side; the disable here is
 * purely UX feedback. Splitting this out of the render function
 * keeps the gating logic unit-testable in the node-only Vitest
 * environment (DOM rendering itself is verified via manual
 * playtest, per the file-header note in `detail-panel.test.ts`).
 */
export function computeDiplomacyButtonStates(
  planet: Planet,
  viewer: number,
  thresholds: { trade: number; federation: number; vassal: number; annex: number },
): DiplomacyButtonStates {
  const ownPool = planet.influence.get(viewer) ?? 0;
  const need = (threshold: number): string =>
    `Need ${threshold} Influence (have ${Math.floor(ownPool)})`;

  // Trade — toggle. Federation lock does NOT block Trade (200 < 400).
  const isTrading = planet.tradingWith.includes(viewer);
  const trade: DiplomacyButtonState = isTrading
    ? { label: 'Stop Trading', action: 'clear-trade', disabledReason: null }
    : {
        label: 'Invoke Trade',
        action: 'invoke-trade',
        disabledReason: ownPool < thresholds.trade ? need(thresholds.trade) : null,
      };

  // Federation — toggle if the viewer is the federation partner.
  let federation: DiplomacyButtonState;
  if (planet.federatedWith === viewer) {
    federation = {
      label: 'End Federation',
      action: 'clear-federation',
      disabledReason: null,
    };
  } else {
    let reason: string | null = null;
    if (planet.federatedWith !== null) {
      reason = 'Already federated to a rival';
    } else if (ownPool < thresholds.federation) {
      reason = need(thresholds.federation);
    }
    federation = {
      label: 'Invoke Federation',
      action: 'invoke-federation',
      disabledReason: reason,
    };
  }

  // Vassal — toggle if the viewer is the suzerain. Federation
  // supersedes Vassal — disabled while ANY federation is active on
  // this planet (matches DIPLOMACY.md §6.4 + invokeVassal validator).
  let vassal: DiplomacyButtonState;
  if (planet.vassalOf === viewer) {
    vassal = {
      label: 'End Vassalage',
      action: 'clear-vassal',
      disabledReason: null,
    };
  } else {
    let reason: string | null = null;
    if (planet.federatedWith !== null) {
      reason = 'Federation supersedes Vassal';
    } else if (ownPool < thresholds.vassal) {
      reason = need(thresholds.vassal);
    }
    vassal = {
      label: 'Invoke Vassal',
      action: 'invoke-vassal',
      disabledReason: reason,
    };
  }

  // Annex — terminal, no toggle. Threshold gate only.
  const annex: DiplomacyButtonState = {
    label: 'Invoke Annex',
    action: 'invoke-annex',
    disabledReason: ownPool < thresholds.annex ? need(thresholds.annex) : null,
  };

  return { trade, federation, vassal, annex };
}

/**
 * Two-row action panel under the Influence section. Row 1 = focus
 * controls (Set Focus toggle + Transfer treasury); row 2 = the four
 * outcome buttons drawn from `computeDiplomacyButtonStates`.
 */
function renderPlanetDiplomacyActions(
  state: GameState,
  planet: Planet,
  viewer: number,
  thresholds: { trade: number; federation: number; vassal: number; annex: number },
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.margin = '8px 0 4px';

  const empire = state.empires.get(viewer)!;
  const isFocused = empire.diplomaticFocus === planet.id;
  const treasuryDiplomatic = Math.floor(empire.resources.diplomatic);

  // ── Row 1 — focus controls ──────────────────────────────────────
  const focusRow = document.createElement('div');
  Object.assign(focusRow.style, {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '6px',
  } as Partial<CSSStyleDeclaration>);

  // Both "Set Focus" and "Clear Focus" go through the same
  // `setDiplomaticFocus` command — the latter passes an empty
  // planetId data attribute that the pointerdown router converts to
  // a `null` PlanetId for the applier.
  const focusBtn = diplomacyActionButton(
    isFocused ? 'Clear Focus' : 'Set Diplomatic Focus',
    'set-diplomatic-focus',
    null,
    {
      empireId: String(viewer),
      planetId: isFocused ? '' : String(planet.id),
    },
  );
  focusRow.appendChild(focusBtn);

  const transferDisabled =
    treasuryDiplomatic <= 0
      ? 'No Diplomatic in treasury'
      : !isFocused
        ? 'Set focus on this planet first'
        : null;
  const transferBtn = diplomacyActionButton(
    `Transfer ${treasuryDiplomatic}`,
    'transfer-diplomatic',
    transferDisabled,
    {
      empireId: String(viewer),
      amount: String(treasuryDiplomatic),
    },
  );
  focusRow.appendChild(transferBtn);
  wrap.appendChild(focusRow);

  // ── Row 2 — outcome buttons ─────────────────────────────────────
  const outcomeRow = document.createElement('div');
  Object.assign(outcomeRow.style, {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  } as Partial<CSSStyleDeclaration>);

  const states = computeDiplomacyButtonStates(planet, viewer, thresholds);
  for (const s of [states.trade, states.federation, states.vassal, states.annex]) {
    outcomeRow.appendChild(
      diplomacyActionButton(s.label, s.action, s.disabledReason, {
        empireId: String(viewer),
        planetId: String(planet.id),
      }),
    );
  }

  wrap.appendChild(outcomeRow);
  return wrap;
}

/**
 * Standard diplomacy button. Style is shared with the existing
 * `makeButton`; disabled visuals + tooltip set when `disabledReason`
 * is non-null. The pointerdown router gates dispatch on the
 * `data-disabled` attribute, so the button still renders but the
 * Command never reaches `applyCommand`.
 */
function diplomacyActionButton(
  label: string,
  action: string,
  disabledReason: string | null,
  dataset: Record<string, string>,
): HTMLButtonElement {
  const btn = makeButton(label);
  btn.dataset.action = action;
  for (const [k, v] of Object.entries(dataset)) btn.dataset[k] = v;
  if (disabledReason !== null) {
    btn.dataset.disabled = '';
    btn.title = disabledReason;
    Object.assign(btn.style, {
      opacity: '0.4',
      cursor: 'not-allowed',
    } as Partial<CSSStyleDeclaration>);
  } else {
    // v4M1.2 — default tooltip on the enabled path. Names mirror
    // `DIPLOMACY.md §6` outcome semantics so the hover always tells
    // the player what the action does, not just that it's available.
    const intent: Record<string, string> = {
      'invoke-annex': 'Annex this planet — flips ownership to you. Permanent.',
      'invoke-trade': 'Open Trade with this planet — adds it to your trade-routes list.',
      'invoke-federation': 'Federate this planet — vision share + Influence cap unlocks.',
      'invoke-vassal': 'Vassalise this planet — directs a fraction of its income to you as tribute.',
      'clear-trade': 'End the current Trade arrangement with this planet.',
      'clear-federation': 'End the Federation with this planet. Vision share persists.',
      'clear-vassal': 'Release this Vassal. Income reverts to the planet.',
      'set-diplomatic-focus': 'Set this planet as your diplomatic focus. Per-tick Diplomatic income now flows into this planet\'s Influence pool.',
      'clear-diplomatic-focus': 'Clear your diplomatic focus. Per-tick Diplomatic income returns to the empire treasury.',
    };
    btn.title = intent[action] ?? `Invoke "${action}" on this planet.`;
  }
  return btn;
}

/** One per-empire row: name + bar + numeric readout. */
function influenceBarRow(
  state: GameState,
  planet: Planet,
  empireId: EmpireId,
  viewer: number,
  thresholds: { trade: number; federation: number; vassal: number; annex: number },
  annexThreshold: number,
): HTMLElement {
  const empire = state.empires.get(empireId);
  const isViewer = empireId === viewer;
  const value = planet.influence.get(empireId) ?? 0;
  const fillColour = empire?.color ?? '#7a8bb5';

  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'grid',
    // 88px label + flexible bar + 96px right-aligned numeric.
    gridTemplateColumns: '88px 1fr 96px',
    alignItems: 'center',
    gap: '8px',
    margin: '4px 0',
    fontSize: '11px',
    color: isViewer ? '#d8e4ff' : '#8ea0c8',
  } as Partial<CSSStyleDeclaration>);

  const label = document.createElement('div');
  label.textContent = isViewer ? 'You' : (empire?.name ?? '?');
  label.style.fontWeight = isViewer ? '600' : '400';
  label.style.overflow = 'hidden';
  label.style.textOverflow = 'ellipsis';
  label.style.whiteSpace = 'nowrap';
  row.appendChild(label);

  // Bar — track + fill + threshold ticks layered on top.
  const track = document.createElement('div');
  Object.assign(track.style, {
    position: 'relative',
    height: '12px',
    background: 'rgba(70, 90, 130, 0.25)',
    borderRadius: '2px',
    overflow: 'hidden',
  } as Partial<CSSStyleDeclaration>);

  const fill = document.createElement('div');
  const fillPct = Math.max(0, Math.min(100, (value / annexThreshold) * 100));
  Object.assign(fill.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    bottom: '0',
    width: `${fillPct}%`,
    background: fillColour,
    opacity: isViewer ? '0.95' : '0.55',
  } as Partial<CSSStyleDeclaration>);
  track.appendChild(fill);

  // Threshold tick marks at trade / federation / vassal (annex is the
  // far-right edge of the bar so no tick needed there).
  for (const t of [thresholds.trade, thresholds.federation, thresholds.vassal]) {
    const tick = document.createElement('div');
    const tickPct = (t / annexThreshold) * 100;
    Object.assign(tick.style, {
      position: 'absolute',
      left: `${tickPct}%`,
      top: '0',
      bottom: '0',
      width: '1px',
      background: 'rgba(220, 230, 255, 0.45)',
    } as Partial<CSSStyleDeclaration>);
    track.appendChild(tick);
  }
  row.appendChild(track);

  const numeric = document.createElement('div');
  numeric.textContent = `${Math.floor(value)} / ${annexThreshold}`;
  numeric.style.fontVariantNumeric = 'tabular-nums';
  numeric.style.textAlign = 'right';
  row.appendChild(numeric);

  return row;
}

/**
 * Three-cell legend strip showing each threshold's name + numeric
 * value, aligned with the bar tick positions above. Annex appears at
 * the far right (= bar end).
 */
function thresholdLegend(
  thresholds: { trade: number; federation: number; vassal: number; annex: number },
  annexThreshold: number,
): HTMLElement {
  const wrap = document.createElement('div');
  Object.assign(wrap.style, {
    display: 'grid',
    gridTemplateColumns: '88px 1fr 96px',
    gap: '8px',
    margin: '4px 0 12px',
    fontSize: '10px',
    color: '#7a8bb5',
    letterSpacing: '0.04em',
  } as Partial<CSSStyleDeclaration>);

  // Empty cell to align with the empire-name column above.
  wrap.appendChild(document.createElement('div'));

  // Inner strip — relative-positioned thresholds at trade / fed /
  // vassal / annex stops. Annex sits flush right; the others use the
  // same percentage scheme as the bar ticks.
  const strip = document.createElement('div');
  strip.style.position = 'relative';
  strip.style.height = '24px';
  for (const [pct, label, value] of [
    [(thresholds.trade / annexThreshold) * 100, 'Trade', thresholds.trade],
    [(thresholds.federation / annexThreshold) * 100, 'Fed', thresholds.federation],
    [(thresholds.vassal / annexThreshold) * 100, 'Vassal', thresholds.vassal],
    [100, 'Annex', thresholds.annex],
  ] as Array<[number, string, number]>) {
    const tick = document.createElement('div');
    Object.assign(tick.style, {
      position: 'absolute',
      left: `${pct}%`,
      top: '0',
      transform: 'translateX(-50%)',
      textAlign: 'center',
      lineHeight: '1.1',
    } as Partial<CSSStyleDeclaration>);
    const arrow = document.createElement('div');
    arrow.textContent = '↑';
    arrow.style.color = 'rgba(220, 230, 255, 0.55)';
    const name = document.createElement('div');
    name.textContent = label;
    const num = document.createElement('div');
    num.textContent = String(value);
    num.style.color = '#5a6c95';
    tick.appendChild(arrow);
    tick.appendChild(name);
    tick.appendChild(num);
    strip.appendChild(tick);
  }
  wrap.appendChild(strip);

  // Empty cell for the right-aligned numeric column above.
  wrap.appendChild(document.createElement('div'));
  return wrap;
}

/** Slim coloured banner used for Federation / Vassal / Trade callouts. */
function diplomacyBanner(text: string, accent: string): HTMLElement {
  const el = document.createElement('div');
  Object.assign(el.style, {
    margin: '8px 0',
    padding: '6px 10px',
    background: 'rgba(20, 30, 50, 0.6)',
    borderLeft: `3px solid ${accent}`,
    fontSize: '11px',
    lineHeight: '1.35',
    color: '#d8e4ff',
    borderRadius: '2px',
  } as Partial<CSSStyleDeclaration>);
  el.textContent = text;
  return el;
}

/**
 * Render the Structures block of the planet detail panel: a line per existing
 * structure (name, level, build/upgrade progress, upgrade button if
 * applicable), followed by a build menu of all allowed structure kinds with
 * per-kind disable state showing WHY a build isn't possible (slot cap,
 * already built unique, not enough resources).
 *
 * Management controls (Upgrade / Build buttons) only show when the viewing
 * empire owns the planet. On enemy / neutral planets only the structure list
 * is visible and read-only. The click handler on the content wrapper checks
 * `data-disabled` before dispatching; rendering a button as visually
 * disabled is sufficient to gate clicks.
 */
function renderPlanetStructures(
  host: HTMLElement,
  state: GameState,
  planet: Planet,
): void {
  const slotCap = maxStructuresForSize(planet.size);
  host.appendChild(
    sectionHeader(`Structures (${planet.structureIds.length} / ${slotCap})`),
  );

  const playerOwns =
    planet.ownerEmpireId !== null && planet.ownerEmpireId === state.playerEmpireId;

  if (planet.structureIds.length === 0) {
    host.appendChild(dim('— none —'));
  } else {
    const empireForRows = playerOwns
      ? state.empires.get(planet.ownerEmpireId!) ?? null
      : null;
    // Compute the "active build" id for this planet: the in-flight
    // structure with the lowest `queuedAt` (ties broken by id) — same
    // ordering advance-structures.ts uses to decide which one ticks.
    // Others are visually marked "queued" so the player doesn't think
    // their build is stalled.
    let activeBuildId: number | null = null;
    let activeQueuedAt = Number.POSITIVE_INFINITY;
    let activeId = Number.POSITIVE_INFINITY;
    for (const id of planet.structureIds) {
      const s = state.structures.get(id);
      if (!s || s.buildTicksRemaining <= 0) continue;
      if (
        s.queuedAt < activeQueuedAt ||
        (s.queuedAt === activeQueuedAt && s.id < activeId)
      ) {
        activeBuildId = s.id;
        activeQueuedAt = s.queuedAt;
        activeId = s.id;
      }
    }
    for (const id of planet.structureIds) {
      const s = state.structures.get(id);
      if (!s) continue;
      host.appendChild(
        renderStructureRow(
          state.structuresData,
          s,
          empireForRows,
          playerOwns,
          s.id === activeBuildId,
        ),
      );
    }
  }

  if (!playerOwns) return;

  const empire = state.empires.get(planet.ownerEmpireId!);
  if (!empire) return;

  host.appendChild(sectionHeader('Build'));
  const menu = document.createElement('div');
  menu.style.display = 'grid';
  menu.style.gridTemplateColumns = '1fr';
  menu.style.gap = '4px';
  for (const kind of BUILDABLE_KINDS) {
    menu.appendChild(renderBuildButton(state, planet, empire, kind));
  }
  host.appendChild(menu);
}

/**
 * Canonical display order for the Build menu: Shipyard first (unblocks M10
 * production so it's the high-leverage build), then Resource boosters, then
 * storage, then Embassy. Matches the reader-mental-model grouping in
 * STRUCTURES §2.
 */
const BUILDABLE_KINDS: readonly StructureKind[] = Object.freeze([
  'shipyard',
  'cloning-center',
  'trade-center',
  'processing-plant',
  'research-lab',
  'refinery',
  'bank',
  'warehouse',
  'fuel-dump',
  'embassy',
  // v3M1 — sensor structure. Sits between Embassy and the v2M6 defensive
  // block so it's adjacent to the other "infrastructure" kinds rather than
  // the militarised stack.
  'long-range-sensor-array',
  // v2M6 — defensive structures appear at the bottom of the menu so the
  // economy basics remain top-of-mind for early-game; players reach for
  // defences after the planet is producing.
  'planetary-shields',
  'underground-bunkers',
  'energy-surface-battery',
  'kinetic-surface-battery',
  // v3M2.2 — Orbital Defence Platform. Slots last in the defensive
  // block; ring deployment is the strongest planet-side defence piece.
  'orbital-defence-platform',
]);

/** Human-readable label for each StructureKind. */
function structureLabel(kind: StructureKind): string {
  switch (kind) {
    case 'shipyard':                return 'Shipyard';
    case 'cloning-center':          return 'Cloning Center';
    case 'trade-center':            return 'Trade Center';
    case 'processing-plant':        return 'Processing Plant';
    case 'research-lab':            return 'Research Lab';
    case 'refinery':                return 'Refinery';
    case 'bank':                    return 'Bank';
    case 'warehouse':               return 'Warehouse';
    case 'fuel-dump':               return 'Fuel Dump';
    case 'embassy':                 return 'Embassy';
    case 'planetary-shields':       return 'Planetary Shields';
    case 'underground-bunkers':     return 'Underground Bunkers';
    case 'energy-surface-battery':  return 'Energy Surface Battery';
    case 'kinetic-surface-battery': return 'Kinetic Surface Battery';
    case 'long-range-sensor-array': return 'Long Range Sensor Array';
    case 'orbital-defence-platform': return 'Orbital Defence Platform';
  }
}

function renderStructureRow(
  sd: StructuresData,
  s: Structure,
  empire: Empire | null,
  playerOwns: boolean,
  isActiveBuild: boolean,
): HTMLElement {
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.justifyContent = 'space-between';
  row.style.alignItems = 'center';
  row.style.gap = '8px';
  row.style.padding = '4px 0';
  row.style.borderBottom = '1px dashed rgba(80,110,160,0.15)';

  const info = document.createElement('div');
  const name = document.createElement('div');
  name.textContent = structureLabel(s.kind);
  name.style.fontWeight = '600';
  const meta = document.createElement('div');
  meta.style.color = '#8ea0c8';
  meta.style.fontSize = '11px';
  if (s.level === 0) {
    meta.textContent = isActiveBuild
      ? `building · ${s.buildTicksRemaining} ticks`
      : `queued · ${s.buildTicksRemaining} ticks`;
  } else if (s.buildTicksRemaining > 0) {
    meta.textContent = isActiveBuild
      ? `L${s.level} · upgrading to L${s.level + 1} · ${s.buildTicksRemaining} ticks`
      : `L${s.level} · upgrade queued · ${s.buildTicksRemaining} ticks`;
  } else {
    meta.textContent = `L${s.level}`;
  }
  info.appendChild(name);
  info.appendChild(meta);
  row.appendChild(info);

  // Upgrade button — shown for any fully-built structure below its
  // per-kind level cap on a player-owned planet, even when the player
  // can't currently afford it. Disabled reason (mid-upgrade / cost)
  // surfaces in the hover title and the cost is always in the label so
  // the player knows the ask. v2M6: Shields and Bunkers cap at L5; the
  // others cap at L6 (default).
  // MFix5.3 — race-gated cap for upgrade-button affordance (Embassy:
  // RACE-AA L2, BB/CC L1).
  if (
    playerOwns &&
    empire &&
    s.level >= 1 &&
    s.level < maxLevelForKindByRace(sd, s.kind, empire.raceId)
  ) {
    const credits = buildCreditsForKind(sd, s.kind);
    const minerals = buildMineralsForKind(sd, s.kind);
    const btn = makeButton(
      `Upgrade → L${s.level + 1} (${credits}C / ${minerals}M)`,
    );
    btn.dataset.action = 'upgrade-structure';
    btn.dataset.empireId = String(empire.id);
    btn.dataset.structureId = String(s.id);
    const reason = upgradeDisabledReason(sd, s, empire);
    if (reason !== null) {
      btn.setAttribute('data-disabled', 'true');
      btn.title = reason;
      btn.style.opacity = '0.45';
      btn.style.cursor = 'not-allowed';
    } else {
      btn.title = `Upgrade this ${s.kind} to level ${s.level + 1}. Costs ${credits} credits and ${minerals} minerals.`;
    }
    row.appendChild(btn);
  }
  return row;
}

/**
 * Mirror of applyUpgradeStructure's validation chain for the UI. Returns
 * a player-facing reason when an upgrade would be rejected, null when
 * it's currently legal. Level cap + level 0 cases are handled at the
 * caller (Upgrade button only renders for L1..L5 builds).
 */
function upgradeDisabledReason(
  sd: StructuresData,
  s: Structure,
  empire: Empire,
): string | null {
  if (s.buildTicksRemaining > 0) return 'Already upgrading';
  const credits = buildCreditsForKind(sd, s.kind);
  const minerals = buildMineralsForKind(sd, s.kind);
  if (empire.resources.credits < credits) return `Need ${credits} credits`;
  if (empire.resources.minerals < minerals) return `Need ${minerals} minerals`;
  return null;
}

/**
 * Render a single build button. Click always fires the delegated handler,
 * but disabled buttons carry `data-disabled` which the handler respects —
 * so the visual state and the click-gate stay in sync from one place.
 */
function renderBuildButton(
  state: GameState,
  planet: Planet,
  empire: Empire,
  kind: StructureKind,
): HTMLElement {
  const sd = state.structuresData;
  const credits = buildCreditsForKind(sd, kind);
  const minerals = buildMineralsForKind(sd, kind);
  const btn = makeButton(
    `+ ${structureLabel(kind)} (${credits}C / ${minerals}M)`,
  );
  btn.dataset.action = 'build-structure';
  btn.dataset.empireId = String(empire.id);
  btn.dataset.planetId = String(planet.id);
  btn.dataset.structureKind = kind;

  const reason = disabledReason(state, planet, empire, kind);
  if (reason !== null) {
    btn.setAttribute('data-disabled', 'true');
    btn.title = reason;
    btn.style.opacity = '0.45';
    btn.style.cursor = 'not-allowed';
  } else {
    btn.title = `Build a ${structureLabel(kind)} on ${planet.name}. Costs ${credits} credits and ${minerals} minerals.`;
  }
  return btn;
}

/**
 * If a build command would be rejected for this kind right now, return the
 * player-facing reason string (shown on hover via title). Returns null when
 * the build is currently legal. Mirrors the validation chain in
 * applyBuildStructure so UI state matches sim behaviour.
 */
function disabledReason(
  state: GameState,
  planet: Planet,
  empire: Empire,
  kind: StructureKind,
): string | null {
  const slotCap = maxStructuresForSize(planet.size);
  if (planet.structureIds.length >= slotCap) return 'Planet structure slots are full';
  const sd = state.structuresData;
  if (isUniqueKind(sd, kind)) {
    for (const id of planet.structureIds) {
      const existing = state.structures.get(id);
      if (existing && existing.kind === kind) {
        return `Only one ${structureLabel(kind)} per planet`;
      }
    }
  }
  const credits = buildCreditsForKind(sd, kind);
  const minerals = buildMineralsForKind(sd, kind);
  if (empire.resources.credits < credits) return `Need ${credits} credits`;
  if (empire.resources.minerals < minerals) return `Need ${minerals} minerals`;
  return null;
}

/**
 * Shared small-button style — uniform across Build and Upgrade so the panel
 * doesn't feel like three separate design languages. Event-delegated at the
 * content wrapper so recreating these on every render is cheap and safe.
 */
function makeButton(label: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  const baseBg = 'rgba(60,100,160,0.35)';
  const baseBorder = 'rgba(120,160,210,0.5)';
  const hoverBg = 'rgba(85,135,200,0.50)';
  const hoverBorder = 'rgba(170,210,255,0.7)';
  Object.assign(btn.style, {
    font: 'inherit',
    color: '#d8e4ff',
    background: baseBg,
    border: `1px solid ${baseBorder}`,
    borderRadius: '3px',
    padding: '3px 8px',
    cursor: 'pointer',
    // v4M1.11 — hover lift + smooth transition. Disabled buttons (set
    // via `data-disabled` + opacity by callers) skip the hover swap so
    // they don't look interactive.
    transition: 'background 100ms ease-out, border-color 100ms ease-out',
  });
  btn.addEventListener('mouseenter', () => {
    if (btn.dataset.disabled === '' || btn.dataset.disabled === 'true') return;
    btn.style.background = hoverBg;
    btn.style.borderColor = hoverBorder;
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = baseBg;
    btn.style.borderColor = baseBorder;
  });
  return btn;
}

// ---------------------------------------------------------------------------
// M10 — Ship-production UI
// ---------------------------------------------------------------------------

/**
 * Render the Shipyard section on a planet's detail panel. Shows:
 *   - the active + pending build queue (if any) with per-entry progress
 *   - a Build Ship menu (player-owned planets only) with one button per
 *     own-race chassis at or below the planet's max Shipyard level
 *
 * Hidden entirely when the planet has no Shipyard AND no queued build —
 * an unimproved world shouldn't clutter the panel with an empty section.
 * If a queue exists (e.g. from an earlier Shipyard that was somehow
 * destroyed, once that's possible), the queue still renders so the player
 * isn't left wondering where their pre-funded order went.
 */
function renderPlanetShipyard(
  host: HTMLElement,
  state: GameState,
  planet: Planet,
): void {
  const playerOwns =
    planet.ownerEmpireId !== null && planet.ownerEmpireId === state.playerEmpireId;
  const classCap = maxShipClassAtPlanet(state, planet.id);
  const hasQueue = planet.shipBuildQueue.length > 0;
  if (classCap === 0 && !hasQueue) return;

  host.appendChild(sectionHeader('Shipyard'));

  if (hasQueue) {
    const queue = document.createElement('div');
    queue.style.marginBottom = '6px';
    for (let i = 0; i < planet.shipBuildQueue.length; i++) {
      const order = planet.shipBuildQueue[i]!;
      const row = document.createElement('div');
      row.style.color = i === 0 ? '#d8e4ff' : '#8ea0c8';
      row.style.fontSize = '11px';
      row.style.padding = '1px 0';
      const prefix = i === 0 ? '▶ ' : '   ';
      row.textContent =
        `${prefix}${order.chassis} · ${order.buildTicksRemaining}/${order.buildTicksTotal} ticks`;
      queue.appendChild(row);
    }
    host.appendChild(queue);
  } else if (playerOwns) {
    host.appendChild(dim('— queue empty —'));
  }

  if (!playerOwns || classCap === 0) return;

  const empire = state.empires.get(planet.ownerEmpireId!);
  if (!empire) return;

  // Gather buildable chassis: player's race, class level ≤ classCap, and
  // NOT a Stationary chassis (those cover both M14 Constructor-built space
  // structures AND the synthetic 'ODP Platform' ring chassis spawned by
  // an orbital-defence-platform Structure activation — none of them are
  // planet-buildable through this Shipyard menu). Stationary is the
  // canonical discriminator: loader stamps it on every injected space-
  // structure template. MFix7.3 swapped this from a `SPACE_STRUCTURE_KINDS`
  // membership check that missed the ring chassis (deliberately absent
  // from that list because the player never constructs it directly),
  // letting it leak into the Build Ship menu. v3M17.1 adds a parallel
  // `SQUADRON_CHASSIS_NAMES` exclusion for the synthetic squadron
  // chassis injected at load time — squadrons aren't Stationary (they're
  // mobile while deployed) but they're spawned by their parent carrier's
  // launch path, never built through any production menu.
  const chassisList: ShipTemplate[] = [];
  for (const tmpl of state.shipTemplates.values()) {
    if (tmpl.raceId !== empire.raceId) continue;
    if (tmpl.classLevel > classCap) continue;
    if (tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)) continue;
    if (SQUADRON_CHASSIS_NAMES.has(tmpl.chassis)) continue;
    chassisList.push(tmpl);
  }
  chassisList.sort(
    (a, b) => a.classLevel - b.classLevel || a.chassis.localeCompare(b.chassis),
  );

  host.appendChild(
    sectionHeader(`Build Ship (class cap ${classCap})`),
  );
  const menu = document.createElement('div');
  menu.style.display = 'grid';
  menu.style.gridTemplateColumns = '1fr';
  menu.style.gap = '4px';
  for (const tmpl of chassisList) {
    menu.appendChild(renderBuildShipButton(state, planet, empire, tmpl));
  }
  host.appendChild(menu);
}

/** Single Build-Ship button with cost in the label + disabled reason in the title. */
function renderBuildShipButton(
  state: GameState,
  planet: Planet,
  empire: Empire,
  tmpl: ShipTemplate,
): HTMLElement {
  const ticks = buildTicksByClass(state.balanceData)[tmpl.classLevel];
  const cost =
    `${tmpl.creditsBuildCost}C / ${tmpl.mineralsBuildCost}M / ${tmpl.popBuildCost}p · ` +
    `${ticks}t`;
  const btn = makeButton(`+ ${tmpl.chassis} (${cost})`);
  btn.dataset.action = 'build-ship';
  btn.dataset.empireId = String(empire.id);
  btn.dataset.planetId = String(planet.id);
  btn.dataset.chassis = tmpl.chassis;

  const reason = buildShipDisabledReason(state, planet, empire, tmpl);
  if (reason !== null) {
    btn.setAttribute('data-disabled', 'true');
    btn.title = reason;
    btn.style.opacity = '0.45';
    btn.style.cursor = 'not-allowed';
  } else {
    btn.title = `Queue a ${tmpl.chassis} build on ${planet.name}. Class ${tmpl.classLevel}. Costs ${tmpl.creditsBuildCost} credits, ${tmpl.mineralsBuildCost} minerals, ${tmpl.popBuildCost} population; ${ticks} ticks to complete.`;
  }
  return btn;
}

/**
 * Mirror of applyBuildShip's validation chain for UI state. Returns a
 * player-facing reason string when the build would be rejected, null when
 * it's legal right now. Shipyard gate is assumed to already be satisfied
 * by the caller (chassis list is filtered by classCap) — we skip
 * re-checking that here to keep the reason messages focused on what the
 * player can actually fix (resources / pop).
 */
function buildShipDisabledReason(
  _state: GameState,
  planet: Planet,
  empire: Empire,
  tmpl: ShipTemplate,
): string | null {
  // Chassis tech gate (M12.3). Class 1 is implicit; Class 2..6 require the
  // corresponding chassis tech. Checked before resource costs so the
  // hover reason points at the actual blocker rather than "you don't have
  // enough money for a ship you can't build anyway".
  if (tmpl.classLevel >= 2) {
    const chassisTech = `Class ${tmpl.classLevel} Starship Chassis`;
    if (!empire.researchedTechs.has(chassisTech)) {
      return `Research ${chassisTech}`;
    }
  }
  if (empire.resources.credits < tmpl.creditsBuildCost) {
    return `Need ${tmpl.creditsBuildCost} credits`;
  }
  if (empire.resources.minerals < tmpl.mineralsBuildCost) {
    return `Need ${tmpl.mineralsBuildCost} minerals`;
  }
  if (planet.currentPopulation < tmpl.popBuildCost) {
    return `Need ${tmpl.popBuildCost} population on this planet`;
  }
  return null;
}

function renderShip(host: HTMLElement, state: GameState, ship: ShipInstance): void {
  const owner = state.empires.get(ship.ownerEmpireId);
  host.appendChild(
    section('SHIP', owner ? `${owner.name} ${ship.chassis}` : ship.chassis),
  );

  const meta = document.createElement('div');
  meta.style.color = '#8ea0c8';
  meta.style.marginBottom = '12px';
  if (ship.systemId !== null) {
    const sys = state.systems.get(ship.systemId);
    meta.textContent = sys ? `in ${sys.name}` : `in system #${ship.systemId}`;
  } else if (ship.jumpTargetSystemId !== null) {
    const target = state.systems.get(ship.jumpTargetSystemId);
    meta.textContent = `jumping to ${target?.name ?? '??'} · ${ship.jumpTicksRemaining} ticks`;
  } else {
    meta.textContent = 'in transit';
  }
  host.appendChild(meta);

  // M8.1-E: cloak-state HUD banner. Painted before the Status block so the
  // "CLOAKED" read is unmissable when it matters, with current energy right
  // next to it (cloak drains per tick — player needs to see the runway
  // before the auto-uncloak triggers at 0).
  if (ship.cloaked) {
    const tmpl = owner
      ? state.shipTemplates.get(`${owner.raceId}:${ship.chassis}`)
      : undefined;
    const cap = tmpl?.energyCapacity ?? null;
    const banner = document.createElement('div');
    Object.assign(banner.style, {
      marginBottom: '8px',
      padding: '6px 8px',
      border: '1px dashed rgba(180, 220, 255, 0.85)',
      borderRadius: '3px',
      color: '#bcdcff',
      fontSize: '11px',
      letterSpacing: '0.08em',
      background: 'rgba(40, 70, 110, 0.25)',
    });
    const energyStr =
      cap !== null
        ? `energy ${ship.currentEnergy.toFixed(0)} / ${cap.toFixed(0)}`
        : `energy ${ship.currentEnergy.toFixed(0)}`;
    banner.textContent = `CLOAKED · ${energyStr}`;
    host.appendChild(banner);
  }

  // v3M15.2 — pull the chassis template up-front so the Status block can
  // surface effective max-hull / max-shields with the active research Mk
  // applied, plus per-feature stats below for space structures.
  const tmpl = owner
    ? state.shipTemplates.get(`${owner.raceId}:${ship.chassis}`)
    : undefined;

  host.appendChild(sectionHeader('Status'));
  if (owner && tmpl) {
    const maxHull = effectiveMaxHull(state, owner, tmpl, ship);
    const hullTech = activeTechName(state, owner, 'Hull');
    host.appendChild(
      labelValue(
        'Hull',
        formatStatValue({
          current: ship.currentHull.toFixed(0),
          max: maxHull.toFixed(0),
          techs: hullTech ? [hullTech] : undefined,
        }),
      ),
    );

    const maxShields = effectiveMaxShields(state, owner, tmpl);
    const shieldRegen = maxShields * shieldRegenRate(state, owner);
    const shieldTech = activeTechName(state, owner, 'Shield');
    host.appendChild(
      labelValue(
        'Shields',
        formatStatValue({
          current: ship.currentShields.toFixed(0),
          max: maxShields.toFixed(0),
          regen: shieldRegen.toFixed(1),
          techs: shieldTech ? [shieldTech] : undefined,
        }),
      ),
    );

    const armor = effectiveArmor(state, owner, tmpl);
    const armorTech = activeTechName(state, owner, 'Armor');
    host.appendChild(
      labelValue(
        'Armor',
        formatStatValue({
          current: armor.toFixed(2),
          techs: armorTech ? [armorTech] : undefined,
        }),
      ),
    );

    if (tmpl.energyCapacity !== null) {
      const regen =
        tmpl.energyRegen !== null ? tmpl.energyRegen.toFixed(1) : undefined;
      host.appendChild(
        labelValue(
          'Energy',
          formatStatValue({
            current: ship.currentEnergy.toFixed(0),
            max: tmpl.energyCapacity.toFixed(0),
            regen,
          }),
        ),
      );
    }

    // v3M15.3 — Special Abilities row (e.g. "Cloaking", "Colonise planet",
    // "Stationary"). Pulled from the chassis template.
    if (tmpl.specialAbilities.length > 0) {
      host.appendChild(labelValue('Abilities', tmpl.specialAbilities.join(', ')));
    }

    // MFix8.5 — surface the chassis's emitted aura on emitter ships
    // (Bulwark / Shaman / Magician). Read-only summary: aura name,
    // range, and the active bonuses. Returns null for ships whose
    // template carries no `aura` field, so non-emitter chassis skip
    // the line entirely.
    const aura = emittedAuraDef(state, ship);
    if (aura) {
      const parts: string[] = [];
      if (aura.shieldMult > 1) {
        parts.push(`+${Math.round((aura.shieldMult - 1) * 100)}% shields`);
      }
      if (aura.shieldRegenMult > 1) {
        parts.push(
          `+${Math.round((aura.shieldRegenMult - 1) * 100)}% shield regen`,
        );
      }
      if (aura.damageMult > 1) {
        parts.push(`+${Math.round((aura.damageMult - 1) * 100)}% damage`);
      }
      if (aura.speedMult > 1) {
        parts.push(`+${Math.round((aura.speedMult - 1) * 100)}% speed`);
      }
      if (aura.stealthBonus > 0) parts.push(`+${aura.stealthBonus} stealth`);
      if (aura.detectionBonus > 0) {
        parts.push(`+${aura.detectionBonus} detection`);
      }
      const summary =
        parts.length > 0
          ? `${aura.name} · range ${aura.range.toFixed(2)} · ${parts.join(', ')}`
          : `${aura.name} · range ${aura.range.toFixed(2)}`;
      host.appendChild(labelValue('Aura', summary));
    }
  } else {
    // Fallback when owner / template is missing (defensive — should not
    // happen in normal play). Render the stripped-down legacy form.
    host.appendChild(labelValue('Hull', `${ship.currentHull.toFixed(0)}`));
    host.appendChild(labelValue('Shields', `${ship.currentShields.toFixed(0)}`));
  }
  if (ship.disabledTicks > 0) {
    host.appendChild(labelValue('Disabled', `${ship.disabledTicks} ticks`));
  }

  // v3M15.2 — Weapon loadout for any chassis that mounts weapons. Hidden
  // for unarmed ships (Constructors, Colonisers, Sensor Buoys, Repair
  // Yards, Minefields, extractors, Warp Gates) by the empty-loadout
  // early-return inside the helper.
  if (tmpl) {
    renderShipWeapons(host, state, owner, tmpl);
  }

  // v3M15.2 — Mobility & Sensors section: sublight speed + hyperdrive
  // fuel use (with active Hyperdrive Efficiency tech) for mobile ships;
  // vision/scan/detection/stealth for every ship since stationary
  // platforms still have meaningful sensor footprints.
  if (tmpl) {
    renderShipMobilitySensors(host, state, owner, tmpl);
  }

  // v3M15.3 — Consolidated Active Orders (bombard / plain move / queued
  // auto-jump). Mid-jump, pending-colonise and active construction
  // surface in their own dedicated sections to avoid duplication.
  renderShipActiveOrders(host, state, ship);

  // v3M15.2 — Space-structure feature stats (Sensor Buoy, Weapons
  // Platforms, Repair Yard, extractors, Warp Gate, Minefield, ODP
  // ring platform). Detected via the STATIONARY_SPECIAL_ABILITY flag
  // stamped on synthetic templates by the loader; mobile ships skip
  // this block entirely.
  if (tmpl && tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)) {
    renderSpaceStructureStats(host, state, ship, owner, tmpl);
  }

  // M14 — Constructor build UI. Only shown when the selected ship is this
  // player's Constructor chassis (per CONSTRUCTOR_CHASSIS_BY_RACE).
  renderConstructorBuildSection(host, state, ship, owner);

  // MFix3.5 — Coloniser affordance: hint text + pending colonise target.
  // Only shown when the ship's template carries the 'Colonise planet'
  // specialAbility AND the ship is owned by the viewer's player empire.
  renderColoniserSection(host, state, ship, owner);

  // v3M22.4 — Ship-commands section: Patrol / Explore / Guard / Stop
  // buttons. Player-owned mobile ships only.
  renderShipCommandsSection(host, state, ship, owner, tmpl);

  // v3M17.4 — Carrier squadron control + status block. Surfaces the
  // 4 docked / N deployed / rebuild progress count, the Attack and
  // Defense Behaviour selectors, and the Launch / Recall override
  // buttons. Player-owned carriers only.
  if (tmpl) {
    renderCarrierSquadronSection(host, state, ship, owner, tmpl);
  }

  // v3M17.4 — Squadron-side stat block (parent carrier + liveFighterCount).
  // Visible on any squadron-chassis ship. No player-owns gate — squadron
  // identity is read-only stat surfacing.
  renderSquadronStats(host, state, ship);
}

/**
 * v3M15.2 — Render the chassis's weapon loadout. Walks
 * `tmpl.weaponLoadout` (already grouped per slot type with counts) and
 * resolves each entry to the highest researched Mk via
 * `resolveSlotWeapon`. The weapon name + Mk encodes the active
 * "technology applied" — players read e.g. "Pulse Laser Mk 4" as both
 * the weapon and the research progress.
 *
 * Hidden when `weaponLoadout` is empty (Constructors, Colonisers, Sensor
 * Buoys, Repair Yards, Minefields, Asteroid Mining Outposts, Gas Giant
 * Refineries, Warp Gates) or when owner is missing.
 */
function renderShipWeapons(
  host: HTMLElement,
  state: GameState,
  owner: Empire | undefined,
  tmpl: ShipTemplate,
): void {
  if (!owner) return;
  if (tmpl.weaponLoadout.length === 0) return;
  host.appendChild(sectionHeader('Weapons'));
  for (const slot of tmpl.weaponLoadout) {
    const weapon = resolveSlotWeapon(slot.weapon, state.weapons, owner.researchedTechs);
    const labelText = slot.count > 1 ? `${slot.count}× ${slot.weapon}` : slot.weapon;
    if (!weapon) {
      host.appendChild(labelValue(labelText, '(unresolved)'));
      continue;
    }
    const cd = cooldownTicksFor(weapon.rateOfFire);
    const cdLabel = Number.isFinite(cd) ? `${cd}t` : '∞';
    const accLabel = `${(weapon.accuracy * 100).toFixed(0)}%`;
    host.appendChild(
      labelValue(
        labelText,
        `${weapon.name} Mk${weapon.mk} · dmg ${weapon.damage.toFixed(0)} · rng ${weapon.range.toFixed(2)} · acc ${accLabel} · cd ${cdLabel}`,
      ),
    );
  }
}

/**
 * v3M15.3 — Consolidated Active Orders block. Surfaces the ship's
 * non-redundant active intents in one place:
 *   - Bombardment: target planet name.
 *   - Sublight movement: destination coords (when the ship has a plain
 *     move order — i.e. no pending auto-jump or pending colonise).
 *   - Pending auto-jump: destination system after arrival (RMB-on-JP
 *     path).
 *
 * Mid-jump status, pending colonise, and active construction are
 * already surfaced elsewhere (meta line for jump; renderColoniserSection
 * for pending colonise; renderConstructorBuildSection for construction)
 * and are deliberately not duplicated here.
 *
 * Hidden when there is nothing active to report.
 */
function renderShipActiveOrders(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
): void {
  const lines: string[] = [];

  if (ship.bombardOrder !== null) {
    const planet = state.planets.get(ship.bombardOrder.targetPlanetId);
    lines.push(`Bombarding ${planet?.name ?? `planet #${ship.bombardOrder.targetPlanetId}`}`);
  }

  if (ship.moveOrder !== null) {
    if (ship.pendingJumpSystemId !== null) {
      const target = state.systems.get(ship.pendingJumpSystemId);
      lines.push(
        `Auto-jump after arrival → ${target?.name ?? 'destination system'}`,
      );
    } else if (ship.pendingColoniseTargetId === null && ship.constructionOrder === null) {
      // Plain move — pending-colonise path is rendered by renderColoniserSection;
      // construction-travel path is rendered by renderConstructorBuildSection.
      const x = ship.moveOrder.targetX.toFixed(2);
      const y = ship.moveOrder.targetY.toFixed(2);
      lines.push(`Moving to (${x}, ${y})`);
    }
  }

  if (lines.length === 0) return;

  host.appendChild(sectionHeader('Active Orders'));
  for (const line of lines) host.appendChild(descriptionBlock(line));
}

/**
 * v3M15.2 — Render the chassis's mobility + sensor stats. Sublight speed
 * and hyperdrive fuel use are skipped for stationary platforms (the
 * synthetic templates set both to 0 — surfacing them would just show
 * confusing zeros). Vision range / scan range / detection / stealth are
 * always shown — Sensor Buoys and other platforms have meaningful
 * sensor footprints even though they don't move.
 *
 * Hyperdrive fuel use applies the empire's Hyperdrive Efficiency Mk
 * multiplier and surfaces the active research tech in brackets when
 * Mk >= 2 (e.g. "7 (Hyperdrive Efficiency Mk 4)").
 */
function renderShipMobilitySensors(
  host: HTMLElement,
  state: GameState,
  owner: Empire | undefined,
  tmpl: ShipTemplate,
): void {
  const isStationary = tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY);
  host.appendChild(sectionHeader('Mobility & Sensors'));

  if (!isStationary) {
    // Sublight speed — apply the empire's Ship Sublight Speed Mk
    // multiplier (MFix7.2) and surface the active research tech in
    // brackets, mirroring the Hyperdrive pattern below. The sim's
    // movement step in advance.ts:advanceShipMovement applies the same
    // multiplier per tick, so the displayed effective speed matches
    // what ships actually do. Without this, the panel reads the raw
    // template value regardless of research, which makes Sublight
    // Engines Mk research look like a no-op even though it isn't.
    if (owner) {
      const effectiveSublight =
        tmpl.sublightSpeed * sublightSpeedMultiplier(state, owner);
      const sublightTech = activeTechName(state, owner, 'Ship Sublight Speed');
      host.appendChild(
        labelValue(
          'Sublight speed',
          formatStatValue({
            current: effectiveSublight.toFixed(3),
            techs: sublightTech ? [sublightTech] : undefined,
          }),
        ),
      );
    } else {
      host.appendChild(labelValue('Sublight speed', tmpl.sublightSpeed.toFixed(3)));
    }
    // v4M5.2b — fuel cost is now distance-scaled per `HYPERSPACE.md §3`.
    // Surface the per-unit rate alongside the range cap so the player
    // can compute jump costs at a glance: `fuel = perUnit × distance`.
    if (owner) {
      const perUnit =
        tmpl.hyperdriveFuelUsePerUnit *
        hyperdriveCostMultiplier(state, owner);
      // v4M5.1 — Hyperdrive Efficiency techs moved from the legacy
      // 'Hyperdrive Fuel Consumption' category into the unified 'Engines'
      // category. Look up the active tech by name prefix instead of
      // category so the UI keeps showing "Active tech: Hyperdrive
      // Efficiency Mk X" against both old + new tech-tree.json shapes.
      const tech = activeTechNameByNamePrefix(state, owner, 'Hyperdrive Efficiency');
      host.appendChild(
        labelValue(
          'Fuel use per unit',
          formatStatValue({
            current: perUnit.toFixed(2),
            techs: tech ? [tech] : undefined,
          }),
        ),
      );
      // v4M5.2b — also surface the per-empire effective range
      // (template baseline × Hyperdrive Range Mk research).
      const rangeMul = hyperdriveRangeMultiplier(state, owner);
      const effectiveRange = tmpl.hyperdriveRange * rangeMul;
      const rangeTech = activeTechNameByNamePrefix(state, owner, 'Hyperdrive Range');
      host.appendChild(
        labelValue(
          'Hyperdrive range',
          formatStatValue({
            current: effectiveRange.toFixed(2),
            techs: rangeTech ? [rangeTech] : undefined,
          }),
        ),
      );
    } else {
      host.appendChild(
        labelValue('Fuel use per unit', tmpl.hyperdriveFuelUsePerUnit.toFixed(2)),
      );
      host.appendChild(
        labelValue('Hyperdrive range', tmpl.hyperdriveRange.toFixed(2)),
      );
    }
  }
  host.appendChild(labelValue('Vision range', tmpl.visionRange.toFixed(2)));
  // MFix8.3 — Scan range surfaces the empire-wide Sensors multiplier
  // (sensorsScanRangeMultiplier) plus the active research tech tag.
  // Detection surfaces the additive Sensors bonus + tech tag. Stealth
  // surfaces the additive Stealth-research bonus + tech tag. All three
  // mirror the Sublight Speed pattern fixed in MFix8.1.
  if (owner) {
    const scanMult = sensorsScanRangeMultiplier(state, owner);
    const sensorsTech = activeTechName(state, owner, 'Non-combat: Sensors');
    host.appendChild(
      labelValue(
        'Scan range',
        formatStatValue({
          current: (tmpl.scanRange * scanMult).toFixed(2),
          techs: sensorsTech ? [sensorsTech] : undefined,
        }),
      ),
    );
    const detectionEff = tmpl.detectionBase + sensorsEmpireBonus(state, owner);
    host.appendChild(
      labelValue(
        'Detection',
        formatStatValue({
          current: detectionEff.toFixed(0),
          techs: sensorsTech ? [sensorsTech] : undefined,
        }),
      ),
    );
    const stealthEff = tmpl.stealthBase + stealthEmpireBonus(state, owner);
    const stealthTech = activeTechName(state, owner, 'Non-combat: Stealth');
    host.appendChild(
      labelValue(
        'Stealth',
        formatStatValue({
          current: stealthEff.toFixed(0),
          techs: stealthTech ? [stealthTech] : undefined,
        }),
      ),
    );
  } else {
    host.appendChild(labelValue('Scan range', tmpl.scanRange.toFixed(2)));
    host.appendChild(labelValue('Detection', tmpl.detectionBase.toFixed(0)));
    host.appendChild(labelValue('Stealth', tmpl.stealthBase.toFixed(0)));
  }
}

/**
 * v3M15.2 — Per-kind feature stats for Constructor-built space
 * structures and ODP ring platforms. Called from renderShip after the
 * standard Status block when the chassis carries the
 * STATIONARY_SPECIAL_ABILITY marker. Each branch surfaces the
 * gameplay-relevant readouts pulled from the v3M14c catalogue
 * (extractor rates, Repair Yard radius, Minefield mechanics, Warp Gate
 * mesh, etc.) so the player understands what the platform actually does.
 *
 * Weapon-loadout details for Weapons Platforms come from the generic
 * `renderShipWeapons` helper above — this branch only adds the framing
 * description.
 */
function renderSpaceStructureStats(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
  owner: Empire | undefined,
  tmpl: ShipTemplate,
): void {
  const sd = state.structuresData;
  const chassis = ship.chassis;

  // ── Sensor Buoy ────────────────────────────────────────────────────
  if (chassis === 'Sensor Buoy') {
    host.appendChild(sectionHeader('Sensor Buoy'));
    host.appendChild(
      descriptionBlock(
        'Stationary scout. Extends the empire’s vision and scan footprint at its in-system position.',
      ),
    );
    host.appendChild(labelValue('Vision range', tmpl.visionRange.toFixed(2)));
    host.appendChild(labelValue('Scan range', tmpl.scanRange.toFixed(2)));
    host.appendChild(labelValue('Detection', tmpl.detectionBase.toFixed(0)));
    return;
  }

  // ── Weapons Platforms (Energy / Kinetic / Concussion) ──────────────
  if (
    chassis === 'Energy Weapons Platform' ||
    chassis === 'Kinetic Weapons Platform' ||
    chassis === 'Concussion Weapons Platform'
  ) {
    host.appendChild(sectionHeader('Weapons Platform'));
    host.appendChild(
      descriptionBlock(
        'Immobile fire-support platform. Engages hostile ships within weapon range; cannot move or jump. Weapon loadout shown above.',
      ),
    );
    return;
  }

  // ── Repair Yard ────────────────────────────────────────────────────
  if (chassis === 'Repair Yard') {
    host.appendChild(sectionHeader('Repair Yard'));
    host.appendChild(
      descriptionBlock(
        'Friendly-hull regeneration field. Any allied ship inside the aura regains hull each tick, capped at the ship’s effective max hull.',
      ),
    );
    host.appendChild(labelValue('Aura radius', repairYardRadius(sd).toFixed(3)));
    host.appendChild(
      labelValue('Hull regen', `+${repairYardHullPerTick(sd).toFixed(1)} / tick / ship`),
    );
    return;
  }

  // ── Asteroid Mining Outpost ───────────────────────────────────────
  if (chassis === 'Asteroid Mining Outpost') {
    host.appendChild(sectionHeader('Asteroid Mining Outpost'));
    host.appendChild(
      descriptionBlock(
        'Resource extractor anchored inside an asteroid field. Adds minerals to the empire’s income each tick (clamped at the storage cap).',
      ),
    );
    host.appendChild(
      labelValue('Mining yield', `+${asteroidMiningMineralsPerTick(sd)} minerals / tick`),
    );
    return;
  }

  // ── Gas Giant Refinery ─────────────────────────────────────────────
  if (chassis === 'Gas Giant Refinery') {
    host.appendChild(sectionHeader('Gas Giant Refinery'));
    host.appendChild(
      descriptionBlock(
        'Resource extractor anchored above a gas giant. Adds fuel to the empire’s income each tick (clamped at the storage cap).',
      ),
    );
    host.appendChild(
      labelValue('Refinery yield', `+${gasGiantRefineryFuelPerTick(sd)} fuel / tick`),
    );
    return;
  }

  // ── Warp Gate ──────────────────────────────────────────────────────
  if (chassis === WARP_GATE_CHASSIS) {
    host.appendChild(sectionHeader('Warp Gate'));
    host.appendChild(
      descriptionBlock(
        'Connection hub. Author one pairwise jump-link to another of your operational Warp Gates from the system selection panel. Building a connection costs minerals + fuel and takes time to establish; once live, friendly ships travel directly between the two gate systems. The Connect button surfaces in v3M18.4.',
      ),
    );
    host.appendChild(
      labelValue(
        'Connection cost',
        `${warpGateConnectionCostMinerals(sd).toLocaleString()} minerals + ${warpGateConnectionCostFuel(sd).toLocaleString()} fuel`,
      ),
    );
    host.appendChild(
      labelValue('Connection build time', `${warpGateConnectionBuildTicks(sd)} ticks`),
    );
    if (owner) {
      let count = 0;
      for (const s of state.ships.values()) {
        if (
          s.chassis === WARP_GATE_CHASSIS &&
          s.ownerEmpireId === owner.id &&
          s.constructionOrder === null
        ) {
          count += 1;
        }
      }
      host.appendChild(labelValue('Owned operational gates', `${count}`));
      // Surface the gate's current connection state if any.
      let linked = false;
      let status: 'building' | 'live' | null = null;
      for (const link of state.warpGateLinks.values()) {
        if (link.gateAId === ship.id || link.gateBId === ship.id) {
          linked = true;
          status = link.status;
          break;
        }
      }
      host.appendChild(
        labelValue(
          'Connection status',
          linked
            ? status === 'live'
              ? 'Connected'
              : 'Connecting…'
            : 'Unconnected',
        ),
      );
    }
    return;
  }

  // ── Minefield ──────────────────────────────────────────────────────
  if (chassis === MINEFIELD_CHASSIS) {
    host.appendChild(sectionHeader('Minefield'));
    host.appendChild(
      descriptionBlock(
        'Depleting hazard zone. Mines trigger on contact with non-friendly ships within radius (each mine throttled per-target). The field despawns when its mine count reaches zero.',
      ),
    );
    const cap = mineCountCap(sd);
    const current = ship.mineCount ?? 0;
    host.appendChild(labelValue('Mines', `${current}/${cap}`));
    host.appendChild(labelValue('Damage per trigger', `${mineDamage(sd)} hull (after armor)`));
    host.appendChild(labelValue('Hazard radius', minefieldRadius(sd).toFixed(3)));
    host.appendChild(
      labelValue(
        'Trigger throttle',
        `${mineTriggerCooldownTicks(sd)} ticks per target`,
      ),
    );

    if (owner) {
      const techs: string[] = [];
      if (owner.researchedTechs.has(MINEFIELDS_SELF_REPLICATING_TECH)) {
        techs.push('Self Replicating Mines (+1/tick regen)');
      }
      if (owner.researchedTechs.has(MINEFIELDS_CLOAKED_TECH)) {
        techs.push('Cloaked Mines (invisible until first triggered)');
      }
      if (owner.researchedTechs.has(MINEFIELDS_IFF_TECH)) {
        techs.push('IFF (skip friendlies)');
      }
      if (techs.length > 0) {
        host.appendChild(sectionHeader('Modifier techs'));
        for (const t of techs) host.appendChild(descriptionBlock(`• ${t}`));
      }
    }
    return;
  }

  // ── ODP Platform (synthetic ring chassis) ──────────────────────────
  if (chassis === ODP_PLATFORM_CHASSIS) {
    host.appendChild(sectionHeader('ODP Ring Platform'));
    host.appendChild(
      descriptionBlock(
        'Ring platform deployed by an Orbital Defence Platform planet structure. Auto-armed with the empire’s best Concussion missile; chassis stats scale with the parent ODP’s level.',
      ),
    );
    const level = ship.chassisLevel ?? 1;
    const multPct = Math.min(2.0, 1.0 + (level - 1) * 0.2) * 100;
    host.appendChild(labelValue('Chassis level', `${level} (${multPct.toFixed(0)}% baseline)`));
    // Find the parent ODP — scan structures, match owner + chassis level.
    // The slot index isn't on the ship; find it via the planet structure
    // for the breadcrumb.
    let parentLabel: string | null = null;
    let slotIndex: number | null = null;
    for (const structure of state.structures.values()) {
      if (structure.kind !== 'orbital-defence-platform') continue;
      if (!structure.ringPlatformShipIds) continue;
      const idx = structure.ringPlatformShipIds.indexOf(ship.id);
      if (idx < 0) continue;
      const planet = state.planets.get(structure.planetId);
      parentLabel = planet ? planet.name : `planet #${structure.planetId}`;
      slotIndex = idx;
      break;
    }
    if (parentLabel !== null && slotIndex !== null) {
      host.appendChild(labelValue('Parent ODP', parentLabel));
      host.appendChild(labelValue('Ring slot', `${slotIndex + 1}/6`));
    }
    return;
  }
}

/**
 * Render the Constructor's active construction status + Build Space
 * Structure menu when the selection is the player empire's Constructor.
 * Buttons set UiState.buildPlacement (placement mode) — the actual
 * buildSpaceStructure command fires when the player clicks a target in
 * the tactical view.
 *
 * Hidden for non-player-owned Constructors and for non-Constructor ships.
 */
function renderConstructorBuildSection(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
  owner: Empire | undefined,
): void {
  if (!owner) return;
  if (owner.id !== state.playerEmpireId) return;
  const constructorChassis = CONSTRUCTOR_CHASSIS_BY_RACE[owner.raceId];
  if (ship.chassis !== constructorChassis) return;

  // Active construction status — travel + build progress.
  if (ship.constructionOrder !== null) {
    const order = ship.constructionOrder;
    host.appendChild(sectionHeader('Constructing'));
    const status = document.createElement('div');
    status.style.padding = '4px 0';
    if (ship.moveOrder !== null) {
      status.textContent = `${order.structureKind} · traveling to (${order.sx.toFixed(2)}, ${order.sy.toFixed(2)})`;
    } else {
      status.textContent =
        `${order.structureKind} · ${order.ticksRemaining}/${order.ticksTotal} ticks`;
    }
    host.appendChild(status);
  }

  // Build Space Structure menu. Available kinds — all of
  // SPACE_STRUCTURE_KINDS. Disabled reasons: resources, already building.
  host.appendChild(sectionHeader('Build Space Structure'));
  if (ship.constructionOrder !== null) {
    host.appendChild(
      dim('— cancel or finish current build to queue another —'),
    );
    return;
  }
  if (ship.cloaked) {
    host.appendChild(dim('— decloak to build —'));
    return;
  }
  if (ship.disabledTicks > 0) {
    host.appendChild(dim('— disabled by EMP —'));
    return;
  }
  if (ship.systemId === null) {
    host.appendChild(dim('— mid-jump —'));
    return;
  }

  const menu = document.createElement('div');
  menu.style.display = 'grid';
  menu.style.gridTemplateColumns = '1fr';
  menu.style.gap = '4px';
  for (const kind of SPACE_STRUCTURE_KINDS) {
    menu.appendChild(renderBuildSpaceStructureButton(state, owner, ship, kind));
  }
  host.appendChild(menu);
}

/**
 * Single Build-Space-Structure button. Click starts placement mode — the
 * actual buildSpaceStructure command fires from input.ts when the
 * player confirms with a tactical-view click. Cost is in the label;
 * resource-insufficient state is shown via disabled hover reason.
 */
function renderBuildSpaceStructureButton(
  state: GameState,
  empire: Empire,
  ship: ShipInstance,
  structureKind: string,
): HTMLElement {
  const sd = state.structuresData;
  const cost = spaceBuildCostForKind(sd, structureKind);
  const ticks = spaceBuildTicksForKind(sd, structureKind);
  const siteRequired = spaceSiteRequirementForKind(sd, structureKind);
  const siteSuffix =
    siteRequired === 'open'
      ? ''
      : ` · needs ${siteRequired === 'asteroid-field' ? 'asteroid field' : 'gas giant'}`;
  const label =
    cost.credits > 0 && ticks > 0
      ? `+ ${structureKind} (${cost.credits}C / ${cost.minerals}M · ${ticks}t${siteSuffix})`
      : `+ ${structureKind}`;
  const btn = makeButton(label);
  btn.dataset.action = 'start-build-placement';
  btn.dataset.constructorShipId = String(ship.id);
  btn.dataset.structureKind = structureKind;

  let reason: string | null = null;
  if (cost.credits > 0) {
    if (empire.resources.credits < cost.credits) {
      reason = `Need ${cost.credits} credits`;
    } else if (empire.resources.minerals < cost.minerals) {
      reason = `Need ${cost.minerals} minerals`;
    }
  }
  // v3M2.1 — Warp Gate is one-per-system-per-player. Surface the gate
  // as the disabled reason if the empire already owns or is constructing
  // one in the Constructor's current system.
  if (
    reason === null &&
    structureKind === WARP_GATE_CHASSIS &&
    ship.systemId !== null
  ) {
    for (const s of state.ships.values()) {
      if (s.ownerEmpireId !== empire.id) continue;
      if (s.systemId !== ship.systemId) continue;
      if (
        s.chassis === WARP_GATE_CHASSIS ||
        (s.constructionOrder !== null &&
          s.constructionOrder.structureKind === WARP_GATE_CHASSIS)
      ) {
        reason = 'Already own a Warp Gate in this system';
        break;
      }
    }
  }
  // MFix10.2 — Minefield is tech-gated. The applier already rejects
  // unresearched Minefield builds (commands.ts §applyBuildSpaceStructure)
  // but the menu silently no-op'd on click without showing why. Mirror
  // the WARP_GATE_CHASSIS pattern: render the button disabled with a
  // tooltip naming the missing prerequisite tech.
  if (
    reason === null &&
    structureKind === MINEFIELD_CHASSIS &&
    !empire.researchedTechs.has(MINEFIELDS_TECH)
  ) {
    reason = 'Requires Minefields tech';
  }
  if (reason !== null) {
    btn.setAttribute('data-disabled', 'true');
    btn.title = reason;
    btn.style.opacity = '0.45';
    btn.style.cursor = 'not-allowed';
  } else {
    btn.title = `Build a ${structureKind} at the Constructor's current location. Costs ${cost.credits} credits and ${cost.minerals} minerals.`;
  }
  return btn;
}

function labelValue(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.justifyContent = 'space-between';
  row.style.gap = '8px';
  row.style.padding = '2px 0';

  const l = document.createElement('span');
  l.textContent = label;
  l.style.color = '#8ea0c8';

  const v = document.createElement('span');
  v.textContent = value;
  v.style.color = '#d8e4ff';
  v.style.textAlign = 'right';

  row.appendChild(l);
  row.appendChild(v);
  return row;
}

/**
 * v3M15.2 — compose a stat value string in the milestone's canonical
 * format: `<current>[/<max>] [+<regen>] [(<tech-name(s)>)]`. Each
 * dimension is omitted when missing/empty so the panel only shows what
 * applies. Numbers are pre-formatted by the caller — no automatic
 * decimal coercion.
 *
 * Examples:
 *   formatStatValue({ current: '1500', max: '2000' })                   = '1500/2000'
 *   formatStatValue({ current: '200', regen: '6', techs: ['Hardened Shield'] })
 *     = '200 +6 (Hardened Shield)'
 *   formatStatValue({ current: '0.45', techs: ['Adamantium Plating'] })
 *     = '0.45 (Adamantium Plating)'
 */
export interface StatValue {
  current: string;
  max?: string;
  regen?: string;
  techs?: readonly string[];
}
export function formatStatValue(v: StatValue): string {
  let out = v.current;
  if (v.max !== undefined) out += `/${v.max}`;
  if (v.regen !== undefined && v.regen !== '' && v.regen !== '0') {
    out += ` +${v.regen}`;
  }
  if (v.techs && v.techs.length > 0) out += ` (${v.techs.join(', ')})`;
  return out;
}

/**
 * v3M15.2 — find the highest-Mk *researched* tech name in the given
 * category. Mk 1 baselines are auto-researched at empire init (M12.2)
 * but the panel suppresses them — they read as "stock chassis", not as
 * a tech upgrade. Returns null when only Mk 1 is present (nothing
 * meaningful to surface in the bracketed segment).
 */
export function activeTechName(
  state: GameState,
  empire: Empire,
  category: string,
): string | null {
  let bestLevel = 0;
  let bestName: string | null = null;
  for (const techName of empire.researchedTechs) {
    const tech = state.techs.get(techName);
    if (!tech || tech.category !== category) continue;
    if (tech.level !== null && tech.level > bestLevel) {
      bestLevel = tech.level;
      bestName = tech.techName;
    }
  }
  return bestLevel >= 2 ? bestName : null;
}

/**
 * v4M5.1 — name-prefix variant of `activeTechName`. Used by surfaces
 * that read techs from a category which now bundles multiple research
 * families (e.g. 'Engines' covers Hyperdrive Speed / Range / Efficiency
 * since the v4M5.1 xlsx rename). Selects the highest-Mk researched tech
 * whose name starts with `namePrefix`; returns null when the empire has
 * only the Mk-1 baseline researched (matches the >=2 gate from
 * `activeTechName` — "no active research" framing).
 */
export function activeTechNameByNamePrefix(
  state: GameState,
  empire: Empire,
  namePrefix: string,
): string | null {
  let bestLevel = 0;
  let bestName: string | null = null;
  for (const techName of empire.researchedTechs) {
    if (!techName.startsWith(namePrefix)) continue;
    const tech = state.techs.get(techName);
    if (!tech) continue;
    if (tech.level !== null && tech.level > bestLevel) {
      bestLevel = tech.level;
      bestName = tech.techName;
    }
  }
  return bestLevel >= 2 ? bestName : null;
}

/**
 * Owner-only Loyalty bar (v2M5). Colour cues bombardment severity at a
 * glance: green ≥ 70, amber 30–69, red < 30. Caller is responsible for
 * the visibility gate (PLANETS §2.7 attacker-can't-see lock); this
 * helper just renders the value.
 */
function loyaltyRow(loyalty: number): HTMLElement {
  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.flexDirection = 'column';
  row.style.gap = '4px';
  row.style.padding = '2px 0';

  const top = document.createElement('div');
  top.style.display = 'flex';
  top.style.justifyContent = 'space-between';
  top.style.gap = '8px';
  const lbl = document.createElement('span');
  lbl.textContent = 'Loyalty';
  lbl.style.color = '#8ea0c8';
  const val = document.createElement('span');
  val.textContent = `${Math.round(loyalty)} / 100`;
  val.style.color = '#d8e4ff';
  val.style.textAlign = 'right';
  top.appendChild(lbl);
  top.appendChild(val);
  row.appendChild(top);

  const track = document.createElement('div');
  track.style.height = '6px';
  track.style.borderRadius = '2px';
  track.style.background = 'rgba(60, 80, 120, 0.35)';
  track.style.overflow = 'hidden';
  const fill = document.createElement('div');
  const pct = Math.max(0, Math.min(100, loyalty));
  fill.style.width = `${pct}%`;
  fill.style.height = '100%';
  // Severity colour. Tuned to match the alert-feed palette intent (UI_UX §1.5):
  // green = good, amber = warning, red = critical.
  let fillColour = '#63c77c'; // green
  if (loyalty < 30) fillColour = '#d24548'; // red
  else if (loyalty < 70) fillColour = '#f0a93b'; // amber
  fill.style.background = fillColour;
  fill.style.transition = 'width 200ms ease, background 200ms ease';
  track.appendChild(fill);
  row.appendChild(track);

  return row;
}

/**
 * MFix3.5 — render the viewer's per-race Habitability score for a planet.
 * Visible on every planet so the player can evaluate a colonisation target
 * before sending a Coloniser. For non-Gaia biomes that the viewer's race
 * lacks the matching Settlement: <Biome> tech for, an additional warning
 * line surfaces (the planet is uncolonisable until that tech is researched,
 * regardless of Habitability score).
 */
function renderPlanetHabitability(
  host: HTMLElement,
  state: GameState,
  planet: Planet,
  viewer: number,
): void {
  const empire = state.empires.get(viewer);
  if (!empire) return;
  const score = habitabilityFor(
    planet,
    empire.raceId,
    empire.researchedTechs,
    state.habitability,
  );
  const band = habitabilityBandFor(score, state.habitability);
  const bandLabel =
    band === 'hostile' ? 'Hostile' : band === 'neutral' ? 'Neutral' : 'Hospitable';
  const bandColour =
    band === 'hostile' ? '#d24548' : band === 'neutral' ? '#f0a93b' : '#63c77c';

  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.justifyContent = 'space-between';
  row.style.gap = '8px';
  row.style.padding = '2px 0';
  const lbl = document.createElement('span');
  lbl.textContent = 'Habitability';
  lbl.style.color = '#8ea0c8';
  const val = document.createElement('span');
  val.textContent = `${Math.round(score)} (${bandLabel})`;
  val.style.color = bandColour;
  val.style.textAlign = 'right';
  row.appendChild(lbl);
  row.appendChild(val);
  host.appendChild(row);

  // Settlement-tech gate hint. Gaia is universally colonisable per
  // PLANETS §2.2 — never gated. Other biomes need the matching tech.
  if (planet.biome !== 'gaia' && planet.ownerEmpireId === null) {
    const xlsxBiome = BIOME_XLSX_LABEL[planet.biome];
    const settlementTech = `Settlement: ${xlsxBiome}`;
    if (!empire.researchedTechs.has(settlementTech)) {
      const note = document.createElement('div');
      note.textContent = `Requires ${settlementTech}`;
      note.style.color = '#f0a93b';
      note.style.fontSize = '11px';
      note.style.fontStyle = 'italic';
      note.style.padding = '2px 0';
      host.appendChild(note);
    }
  }
}

/**
 * MFix4.3 — render the Terraform-start affordance on the planet detail
 * panel. Two paths share this single button:
 *
 *   1. **Coloniser path** (Terraforming L4): planet uninhabited + non-Gaia,
 *      empire has Terraforming researched, a player-owned Coloniser is
 *      within colonisation range. Click despatches `startTerraform` with
 *      the Coloniser's id; the ship is consumed on success.
 *
 *   2. **Planet-only path** (Advanced Terraforming L6): planet owned by
 *      the viewer + non-Gaia, empire has Advanced Terraforming
 *      researched. Click despatches with `coloniserShipId: null`.
 *
 * The button is hidden entirely when neither path's preconditions hold;
 * a disabled-but-visible state with reason text is reserved for v3M11
 * polish (matches how the build menu surfaces disable reasons).
 */
function renderTerraformSection(
  host: HTMLElement,
  state: GameState,
  planet: Planet,
  viewer: number,
): void {
  if (planet.biome === 'gaia') return; // already at target — no terraform needed
  const empire = state.empires.get(viewer);
  if (!empire) return;

  let coloniserShipId: number | null = null;
  let pathLabel = '';
  let canStart = false;

  // Path 1 — Coloniser (Terraforming L4) for uninhabited non-Gaia planets.
  const isUninhabited = planet.ownerEmpireId === null && planet.currentPopulation === 0;
  if (
    isUninhabited &&
    empire.researchedTechs.has(TERRAFORMING_TECH) &&
    empire.resources.minerals >= state.balanceData.terraforming.mineralsCost &&
    empire.resources.fuel >= state.balanceData.terraforming.fuelCost
  ) {
    const range = colonisationRangeFor(planet.size, state.balanceData);
    const rangeSq = range * range;
    for (const ship of state.ships.values()) {
      if (ship.ownerEmpireId !== viewer) continue;
      if (ship.systemId !== planet.systemId) continue;
      const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
      if (!tmpl) continue;
      if (!tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) continue;
      const dx = ship.sx - planet.systemX;
      const dy = ship.sy - planet.systemY;
      if (dx * dx + dy * dy > rangeSq) continue;
      coloniserShipId = ship.id;
      pathLabel = 'Terraform to Gaia (Coloniser consumed)';
      canStart = true;
      break;
    }
  }

  // Path 2 — Advanced Terraforming L6 for owned non-Gaia planets.
  if (
    !canStart &&
    planet.ownerEmpireId === viewer &&
    empire.researchedTechs.has(ADVANCED_TERRAFORMING_TECH) &&
    empire.resources.minerals >= state.balanceData.terraforming.mineralsCost &&
    empire.resources.fuel >= state.balanceData.terraforming.fuelCost
  ) {
    pathLabel = 'Terraform to Gaia';
    canStart = true;
  }

  if (!canStart) return;

  host.appendChild(sectionHeader('Terraforming'));
  const costLine = document.createElement('div');
  costLine.textContent = `Cost: ${state.balanceData.terraforming.mineralsCost} minerals · ${state.balanceData.terraforming.fuelCost} fuel`;
  costLine.style.color = '#8ea0c8';
  costLine.style.fontSize = '11px';
  costLine.style.padding = '2px 0';
  host.appendChild(costLine);

  const btn = makeButton(pathLabel);
  btn.dataset.action = 'start-terraform';
  btn.dataset.empireId = String(viewer);
  btn.dataset.planetId = String(planet.id);
  btn.dataset.coloniserShipId = coloniserShipId === null ? '' : String(coloniserShipId);
  host.appendChild(btn);
}

/**
 * MFix3.5 — render the Coloniser-specific affordance block when the
 * selected ship belongs to the viewer empire and carries the
 * "Colonise planet" specialAbility. Shows the colonise hint and the
 * pending target name (when one is set), so the player has feedback
 * after issuing a sail-then-colonise order via RMB.
 */
function renderColoniserSection(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
  owner: Empire | undefined,
): void {
  if (!owner) return;
  if (ship.ownerEmpireId !== state.playerEmpireId) return;
  const tmpl = state.shipTemplates.get(`${owner.raceId}:${ship.chassis}`);
  if (!tmpl) return;
  if (!tmpl.specialAbilities.includes(COLONISER_SPECIAL_ABILITY)) return;

  host.appendChild(sectionHeader('Coloniser'));

  if (ship.pendingColoniseTargetId !== null) {
    const target = state.planets.get(ship.pendingColoniseTargetId);
    const name = target ? target.name : `planet #${ship.pendingColoniseTargetId}`;
    host.appendChild(labelValue('En route to', name));
  } else {
    const hint = document.createElement('div');
    hint.textContent = 'Right-click an uninhabited planet to colonise it.';
    hint.style.color = '#8ea0c8';
    hint.style.fontSize = '11px';
    hint.style.fontStyle = 'italic';
    hint.style.padding = '2px 0';
    host.appendChild(hint);
  }
}

/**
 * v3M22.4 — Ship-commands control block. Shown only when the selected
 * ship is a player-owned mobile ship (not stationary, not a squadron).
 * Surfaces:
 *   - Current command status line (e.g. "Active: Patrol (3 waypoints)",
 *     "Active: Guard — Sirius II", "Active: Explore", "Idle").
 *   - Patrol button: dispatches setShipPatrol. Disabled when
 *     waypoints.length === 0 (the patrol would no-op).
 *   - Explore button: dispatches setShipExplore.
 *   - Guard button: enters guard target-mode (UI-only mutation).
 *   - Stop button: dispatches clearShipCommand. Disabled when
 *     commandKind === 'idle'.
 *
 * The buttons use the existing event-delegation pattern via
 * `data-action` attributes; click handlers are wired in the parent
 * pointerdown listener.
 */
function renderShipCommandsSection(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
  owner: Empire | undefined,
  tmpl: ShipTemplate | undefined,
): void {
  if (!owner) return;
  if (owner.id !== state.playerEmpireId) return; // player ships only
  if (!tmpl) return;
  if (tmpl.specialAbilities.includes(STATIONARY_SPECIAL_ABILITY)) return;
  if (ship.parentCarrierShipId !== undefined) return; // squadrons skip
  if (ship.systemId === null) return; // mid-jump
  if (ship.currentHull <= 0) return;

  host.appendChild(sectionHeader('Commands'));

  // Status line — what's the ship currently doing per its v3M22 state.
  let status = 'Idle';
  if (ship.commandKind === 'patrol') {
    status = `Patrol · ${ship.waypoints.length} waypoint${ship.waypoints.length === 1 ? '' : 's'}`;
  } else if (ship.commandKind === 'explore') {
    status = 'Explore';
  } else if (ship.commandKind === 'guard') {
    if (ship.guardTargetKind === 'planet' && ship.guardTargetId !== null) {
      const target = state.planets.get(ship.guardTargetId);
      status = `Guard · ${target?.name ?? '?'}`;
    } else if (ship.guardTargetKind === 'ship' && ship.guardTargetId !== null) {
      const target = state.ships.get(ship.guardTargetId);
      status = `Guard · ${target?.chassis ?? '?'} #${ship.guardTargetId}`;
    } else {
      status = 'Guard';
    }
  }
  host.appendChild(labelValue('Active', status));

  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    gap: '6px',
    marginTop: '6px',
    flexWrap: 'wrap',
  });
  host.appendChild(row);

  const patrolBtn = makeButton(
    ship.waypoints.length === 0
      ? 'Patrol (no waypoints)'
      : 'Patrol',
  );
  patrolBtn.dataset.action = 'set-ship-patrol';
  patrolBtn.dataset.empireId = String(owner.id);
  patrolBtn.dataset.shipId = String(ship.id);
  if (ship.waypoints.length === 0) {
    patrolBtn.dataset.disabled = '1';
    patrolBtn.style.opacity = '0.55';
    patrolBtn.style.cursor = 'not-allowed';
    patrolBtn.title = 'Shift+RMB on the system view to drop a waypoint first.';
  } else {
    patrolBtn.title =
      `Cycle this ship between its ${ship.waypoints.length} waypoint${ship.waypoints.length === 1 ? '' : 's'} indefinitely. Engages hostiles within combat range.`;
  }
  row.appendChild(patrolBtn);

  const exploreBtn = makeButton('Explore');
  exploreBtn.dataset.action = 'set-ship-explore';
  exploreBtn.dataset.empireId = String(owner.id);
  exploreBtn.dataset.shipId = String(ship.id);
  exploreBtn.title =
    'Auto-jump to unscouted systems until all reachable destinations have been visited.';
  row.appendChild(exploreBtn);

  const guardBtn = makeButton('Guard…');
  guardBtn.dataset.action = 'start-ship-guard';
  guardBtn.dataset.shipId = String(ship.id);
  guardBtn.dataset.systemId = String(ship.systemId);
  guardBtn.title = 'Click a planet or friendly ship to lock the guard target.';
  row.appendChild(guardBtn);

  const stopBtn = makeButton('Stop');
  stopBtn.dataset.action = 'clear-ship-command';
  stopBtn.dataset.empireId = String(owner.id);
  stopBtn.dataset.shipId = String(ship.id);
  if (ship.commandKind === 'idle') {
    stopBtn.dataset.disabled = '1';
    stopBtn.style.opacity = '0.55';
    stopBtn.style.cursor = 'not-allowed';
    stopBtn.title = 'No active command to cancel.';
  } else {
    stopBtn.title = `Cancel the current ${ship.commandKind} command. Ship returns to idle.`;
  }
  row.appendChild(stopBtn);
}

/**
 * v3M17.4 — Carrier squadron control + status block.
 *
 * Shown only when the selected ship is a carrier-template chassis owned
 * by the viewer's player empire. Surfaces:
 *   - Status: docked count / deployed count / rebuild progress.
 *   - Attack Behaviour selector (none / visual-range / scan-range).
 *   - Defense Behaviour selector (return-at-low / no-surrender).
 *   - Launch all squadrons button (when docked > 0 and none own deployed).
 *   - Recall all squadrons button (when any own deployed).
 *
 * Behaviour pickers are 3-button / 2-button radio groups (not <select>)
 * to match the existing detail-panel button-driven UX.
 */
function renderCarrierSquadronSection(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
  owner: Empire | undefined,
  tmpl: ShipTemplate,
): void {
  if (!owner) return;
  if (ship.ownerEmpireId !== state.playerEmpireId) return;
  if (!isCarrierTemplate(tmpl)) return;

  const docked = ship.embarkedSquadronCount ?? 0;
  const rebuildProgress = ship.rebuildProgressFighters ?? 0;
  const attack = ship.carrierAttackBehaviour ?? 'visual-range';
  const defense = ship.carrierDefenseBehaviour ?? 'return-at-low';

  // Count deployed own squadrons.
  let deployed = 0;
  for (const other of state.ships.values()) {
    if (other.parentCarrierShipId === ship.id) deployed++;
  }

  host.appendChild(sectionHeader('Carrier Squadrons'));

  host.appendChild(
    labelValue('Docked', `${docked} / ${CARRIER_SQUADRON_CAPACITY}`),
  );
  host.appendChild(labelValue('Deployed', String(deployed)));
  if (docked < CARRIER_SQUADRON_CAPACITY) {
    host.appendChild(
      labelValue(
        'Rebuild progress',
        `${rebuildProgress} / ${SQUADRON_FIGHTER_COUNT} fighters`,
      ),
    );
  }

  // Attack Behaviour radio group.
  host.appendChild(behaviourLabel('Attack Behaviour'));
  const attackRow = document.createElement('div');
  attackRow.style.display = 'flex';
  attackRow.style.gap = '4px';
  attackRow.style.marginBottom = '6px';
  attackRow.appendChild(
    behaviourButton('None', 'set-carrier-attack', 'none', attack, owner.id, ship.id),
  );
  attackRow.appendChild(
    behaviourButton(
      'Visual range',
      'set-carrier-attack',
      'visual-range',
      attack,
      owner.id,
      ship.id,
    ),
  );
  attackRow.appendChild(
    behaviourButton(
      'Scan range',
      'set-carrier-attack',
      'scan-range',
      attack,
      owner.id,
      ship.id,
    ),
  );
  host.appendChild(attackRow);

  // Defense Behaviour radio group.
  host.appendChild(behaviourLabel('Defense Behaviour'));
  const defenseRow = document.createElement('div');
  defenseRow.style.display = 'flex';
  defenseRow.style.gap = '4px';
  defenseRow.style.marginBottom = '8px';
  defenseRow.appendChild(
    behaviourButton(
      'Return at low',
      'set-carrier-defense',
      'return-at-low',
      defense,
      owner.id,
      ship.id,
    ),
  );
  defenseRow.appendChild(
    behaviourButton(
      'No surrender',
      'set-carrier-defense',
      'no-surrender',
      defense,
      owner.id,
      ship.id,
    ),
  );
  host.appendChild(defenseRow);

  // Manual override buttons. Mutually exclusive — Launch is only visible
  // when there are docked squadrons AND no own currently deployed;
  // Recall is only visible when any own squadron is deployed.
  if (deployed === 0 && docked > 0) {
    host.appendChild(
      bigActionButton(
        `Launch all squadrons (${docked})`,
        'launch-carrier-squadrons',
        owner.id,
        ship.id,
      ),
    );
  } else if (deployed > 0) {
    host.appendChild(
      bigActionButton(
        `Recall all squadrons (${deployed})`,
        'recall-carrier-squadrons',
        owner.id,
        ship.id,
      ),
    );
  }
}

/** Small label used above each behaviour radio group. */
function behaviourLabel(text: string): HTMLDivElement {
  const div = document.createElement('div');
  div.textContent = text;
  div.style.color = '#8ea0c8';
  div.style.fontSize = '11px';
  div.style.marginTop = '6px';
  div.style.marginBottom = '3px';
  return div;
}

/**
 * Single button in a behaviour radio group. Highlights when its
 * `behaviourValue` matches the carrier's `currentBehaviour`. Click
 * dispatches the matching `set-carrier-attack` / `set-carrier-defense`
 * action through the panel's pointerdown handler.
 */
function behaviourButton(
  label: string,
  action: 'set-carrier-attack' | 'set-carrier-defense',
  behaviourValue: string,
  currentBehaviour: string,
  empireId: number,
  carrierShipId: number,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.dataset.action = action;
  btn.dataset.behaviour = behaviourValue;
  btn.dataset.empireId = String(empireId);
  btn.dataset.carrierShipId = String(carrierShipId);
  btn.title = action === 'set-carrier-attack'
    ? `Set this carrier's attack behaviour to "${label}".`
    : `Set this carrier's defense behaviour to "${label}".`;
  const active = behaviourValue === currentBehaviour;
  const inactiveBg = 'rgba(40, 60, 90, 0.4)';
  const inactiveBorder = 'rgba(80, 110, 160, 0.4)';
  const activeBg = 'rgba(80, 130, 200, 0.5)';
  const activeBorder = 'rgba(180, 220, 255, 0.7)';
  const hoverBg = 'rgba(60, 90, 140, 0.55)';
  const hoverBorder = 'rgba(130, 170, 220, 0.65)';
  Object.assign(btn.style, {
    flex: '1 1 0',
    padding: '4px 6px',
    fontSize: '11px',
    cursor: 'pointer',
    background: active ? activeBg : inactiveBg,
    border: `1px solid ${active ? activeBorder : inactiveBorder}`,
    color: '#d8e4ff',
    borderRadius: '2px',
    // v4M1.11 — smooth state changes and hover lift.
    transition: 'background 100ms ease-out, border-color 100ms ease-out',
  });
  // Hover lift — only when the button is inactive (the active button is
  // already lit; hovering it would muddy the state signal).
  btn.addEventListener('mouseenter', () => {
    if (behaviourValue === currentBehaviour) return;
    btn.style.background = hoverBg;
    btn.style.borderColor = hoverBorder;
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = behaviourValue === currentBehaviour ? activeBg : inactiveBg;
    btn.style.borderColor = behaviourValue === currentBehaviour ? activeBorder : inactiveBorder;
  });
  return btn;
}

/** Full-width action button used for Launch / Recall overrides. */
function bigActionButton(
  label: string,
  action: 'launch-carrier-squadrons' | 'recall-carrier-squadrons',
  empireId: number,
  carrierShipId: number,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  btn.dataset.action = action;
  btn.dataset.empireId = String(empireId);
  btn.dataset.carrierShipId = String(carrierShipId);
  btn.title = action === 'launch-carrier-squadrons'
    ? 'Manually launch all docked squadrons regardless of attack behaviour.'
    : 'Manually recall all deployed squadrons back to this carrier.';
  const baseBg = 'rgba(80, 130, 200, 0.4)';
  const baseBorder = 'rgba(180, 220, 255, 0.6)';
  const hoverBg = 'rgba(110, 160, 220, 0.55)';
  const hoverBorder = 'rgba(210, 235, 255, 0.85)';
  Object.assign(btn.style, {
    width: '100%',
    padding: '6px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    background: baseBg,
    border: `1px solid ${baseBorder}`,
    color: '#d8e4ff',
    borderRadius: '2px',
    marginTop: '4px',
    // v4M1.11 — hover lift for full-width carrier actions.
    transition: 'background 100ms ease-out, border-color 100ms ease-out',
  });
  btn.addEventListener('mouseenter', () => {
    btn.style.background = hoverBg;
    btn.style.borderColor = hoverBorder;
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = baseBg;
    btn.style.borderColor = baseBorder;
  });
  return btn;
}

/**
 * v3M17.4 — Squadron-side stat block. Surfaces the parent-carrier link
 * and the surviving fighter count for any squadron-chassis ship.
 * Read-only — no action buttons; manual recall is on the parent carrier
 * panel.
 */
function renderSquadronStats(
  host: HTMLElement,
  state: GameState,
  ship: ShipInstance,
): void {
  if (!SQUADRON_CHASSIS_NAMES.has(ship.chassis)) return;
  host.appendChild(sectionHeader('Squadron'));
  const live = ship.liveFighterCount ?? 0;
  host.appendChild(
    labelValue('Surviving fighters', `${live} / ${SQUADRON_FIGHTER_COUNT}`),
  );
  if (ship.parentCarrierShipId !== undefined) {
    const carrier = state.ships.get(ship.parentCarrierShipId);
    const label = carrier
      ? `${carrier.chassis} #${carrier.id}`
      : `(carrier #${ship.parentCarrierShipId} not found)`;
    host.appendChild(labelValue('Parent carrier', label));
  }
}
